import React, { useState, useRef, useEffect, useMemo } from 'react';
import SwotAnalysis from './SwotAnalysis';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  Box,
  Flex,
  Icon,
  Link,
  Text,
  VStack,
  HStack,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  SimpleGrid,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Card,
  CardBody,
  CardHeader,
  Badge,
  useColorModeValue,
  Avatar,
  Divider,
  Progress,
  Button,
  IconButton,
  Image,
  Skeleton,
  Spinner,
  Tooltip as ChakraTooltip,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useCustomToast } from '../utils/customToast';
import { StarIcon, ExternalLinkIcon, PhoneIcon } from '@chakra-ui/icons';
import {
  FaMapMarkerAlt,
  FaGlobe,
  FaChartLine,
  FaSmile,
  FaMeh,
  FaFrown,
  FaRegStar,
  FaStar,
  FaChartPie,
  FaComments,
  FaThumbsUp,
  FaThumbsDown,
  FaSearch
} from 'react-icons/fa';
import {
  HiOutlineTrendingUp,
  HiOutlineHashtag,
  HiOutlineEmojiHappy
} from 'react-icons/hi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
  Cell,
  PieChart,
  Pie,
  ReferenceLine
} from 'recharts';
import { use } from 'react';
import { useUser } from '../contexts/UserContext';
import { logger } from '../utils/logger';
import { getImagesByBusiness, getBusinessImages } from '../services/imageService';
import { getHistoricalTraffic } from '../services/reviewService';
import LocalyticsScoreCard from './LocalyticsScoreCard';
import LocalyticsRankingCard from './LocalyticsRankingCard';
import GetMoreCustomersCard from './GetMoreCustomersCard';
import KeepMoreCustomersCard from './KeepMoreCustomersCard';
import BusinessReviewOverviewSection from './BusinessReviewOverviewSection';
import { getBusinessPricing, formatPriceLevel, getPriceLevelColor } from '../services/pricingService';
import {
  formatTrafficDisplay,
  calculateSeoScore,
  calculateSeoScoreOrEstimate,
  isSeoScoreEstimated,
  estimateSeoScore,
  getSeoScoreColor,
  getAdHistory,
  formatSearchDate,
  getBusinessAdRank,
  rankBusinessesByAdCount,
} from '../utils/competitiveIntelHelpers';
import { generateBusinessSwotInsights, getSwotEvidence } from '../services/aiKeywordService';
import { EXAMPLE_DEMO_IMAGES } from '../data/exampleCompetitorMapData';
import { FaDollarSign, FaQuoteLeft, FaBullhorn, FaGoogle, FaFilePdf, FaTrophy, FaUsers, FaChartBar, FaArrowUp, FaArrowDown, FaCircle } from 'react-icons/fa';
import { generateScreenshotPdf } from '../utils/pdfExport';
import { DEMO_BUSINESS_REVIEW_DATA } from '../utils/businessReviewDemoProps';

// Compact stat pill used in the Ads Running section
const StatPill = ({ icon, color = 'gray', label, value, sublabel }) => (
  <HStack
    bg='white'
    borderWidth='1px'
    borderColor='gray.100'
    borderRadius='lg'
    px={2.5}
    py={1.5}
    spacing={2}
    align='center'
    position='relative'
    overflow='hidden'
    _before={{
      content: '""',
      position: 'absolute',
      left: 0,
      top: 1.5,
      bottom: 1.5,
      width: '3px',
      bg: `${color}.400`,
      borderRightRadius: 'sm',
    }}
  >
    <Flex
      w='22px'
      h='22px'
      bg={`${color}.50`}
      borderRadius='md'
      justify='center'
      align='center'
      flexShrink={0}
      ml={1}
    >
      <Icon as={icon} color={`${color}.500`} boxSize={2.5} />
    </Flex>
    <VStack align='start' spacing={0} flex='1' minW={0}>
      <Text
        fontSize='3xs'
        fontWeight='700'
        color='gray.500'
        textTransform='uppercase'
        letterSpacing='0.06em'
        lineHeight='1.1'
        noOfLines={1}
      >
        {label}
      </Text>
      <HStack spacing={1.5} align='baseline' w='100%'>
        <Text
          fontSize='md'
          fontWeight='700'
          color='gray.800'
          lineHeight='1.15'
        >
          {value}
        </Text>
        {sublabel && (
          <Text fontSize='2xs' color='gray.400' noOfLines={1} flex='1' minW={0}>
            {sublabel}
          </Text>
        )}
      </HStack>
    </VStack>
  </HStack>
);

// Emoji mapping for business types
const typeEmojis = {
  restaurant: '🍽️',
  wine_bar: '🍷',
  vegan_restaurant: '🌱',
  vegetarian_restaurant: '🥗',
  bar: '🍸',
  food: '🍴',
  cafe: '☕',
  market: '🏪',
  deli: '🥪',
  asian_restaurant: '🥢',
  thai_restaurant: '🇹🇭',
  indian_restaurant: '🍛',
  mediterranean_restaurant: '🫒',
  american_restaurant: '🍔',
  fine_dining_restaurant: '✨',
  brunch_restaurant: '🍳',
  middle_eastern_restaurant: '🧆',
  chinese_restaurant: '🥡',
  meal_takeaway: '📦',
  food_store: '🏪',
  catering_service: '👨‍🍳',
  food_delivery: '🚚'
};

// Helper function to generate comparative text for a metric
const getComparativeText = (rank, total) => {
  if (rank === 'N/A' || total === 'N/A') return 'ranking unavailable';
  
  const percentile = ((total - rank + 1) / total) * 100;
  
  if (rank === 1) return `leading the market at #1 out of ${total} competitors`;
  if (rank <= 3) return `in the top 3 out of ${total} competitors`;
  if (rank <= 5) return `in the top 5 out of ${total} competitors`;
  if (percentile >= 75) return `ranking in the top 25% (#${rank} of ${total})`;
  if (percentile >= 50) return `ranking in the upper half (#${rank} of ${total})`;
  if (percentile >= 25) return `ranking in the lower half (#${rank} of ${total})`;
  return `ranking #${rank} out of ${total} competitors`;
};

// Helper function to convert number to ordinal (1st, 2nd, 3rd, etc.)
const getOrdinal = (n) => {
  if (n === 'N/A') return 'N/A';
  const num = parseInt(n, 10);
  if (isNaN(num)) return n;
  const s = ['th', 'st', 'nd', 'rd'];
  const v = num % 100;
  return num + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Helper function to calculate points for a category rank (used in Localytics Score)
// 1st = 20 points, 2nd = 19, ..., 20th = 1 point, 21st+ = 0 points
const getCategoryPoints = (rank) => {
  if (rank === 'N/A' || !rank || isNaN(rank)) return 0;
  return Math.max(0, 21 - rank);
};

// Gift Ribbon Component - minimal corner badge
const GiftRibbon = ({ isTop5, label }) => {
  return (
    <Box
      position='absolute'
      top='10px'
      right='10px'
      zIndex={2}
      pointerEvents='none'
    >
      <Badge
        bg={isTop5 ? 'green.50' : 'red.50'}
        color={isTop5 ? 'green.600' : 'red.600'}
        fontSize='9px'
        fontWeight='600'
        px={2}
        py={0.5}
        borderRadius='full'
        textTransform='uppercase'
        letterSpacing='0.5px'
        border='1px solid'
        borderColor={isTop5 ? 'green.200' : 'red.200'}
      >
        {label}
      </Badge>
    </Box>
  );
};

const BusinessReviewModal = ({
  isOpen,
  onClose,
  business: businessProp,
  yearData: yearDataProp,
  sortedByTotal: sortedByTotalProp,
  sortedBySentiment: sortedBySentimentProp,
  sortedByGrowth: sortedByGrowthProp,
  sortedByTraffic: sortedByTrafficProp,
  sortedBySeo: sortedBySeoProp,
  demoMode = false,
  demoTrafficHistory: demoTrafficHistoryProp = null,
  demoPricingData: demoPricingDataProp = null,
  compact = false,
  inline = false,
  flush = false,
  hideCompetitorComparison = false,
  onSelectBusiness = null,
}) => {
  const demoDefaults = demoMode ? DEMO_BUSINESS_REVIEW_DATA : null;
  const yearData = yearDataProp ?? demoDefaults?.yearData;
  const sortedByTotal = sortedByTotalProp ?? demoDefaults?.sortedByTotal;
  const sortedBySentiment = sortedBySentimentProp ?? demoDefaults?.sortedBySentiment;
  const sortedByGrowth = sortedByGrowthProp ?? demoDefaults?.sortedByGrowth;
  const sortedByTraffic = sortedByTrafficProp ?? demoDefaults?.sortedByTraffic;
  const sortedBySeo = sortedBySeoProp ?? demoDefaults?.sortedBySeo;
  const demoTrafficHistory = demoTrafficHistoryProp ?? demoDefaults?.demoTrafficHistory;
  const demoPricingData = demoPricingDataProp ?? demoDefaults?.demoPricingData;
  const boxRef = useRef(null);
  const [width, setWidth] = useState(boxRef.current?.offsetWidth || 0);
  const { toggleFavorite, isFavorited } = useUser();
  const toast = useCustomToast();
  const navigate = useNavigate();

  // Allow users to peek at a neighboring competitor from inside the modal
  // (Competitors card). When set, the modal renders that competitor in
  // place of the originally-opened `businessProp`. It is automatically
  // cleared whenever the parent opens the modal for a different business.
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const business = selectedCompetitor || businessProp;

  // Navigate to a related dashboard from a clicked container.
  // Closes the modal first so the destination dashboard is fully visible
  // and skips navigation in demo mode (where dashboard routes are gated
  // behind authentication).
  const navigateToDashboard = (path) => {
    if (demoMode || !path) return;
    if (onClose) onClose();
    navigate(path);
  };

  // Default Tooltip props shared by every "Click for Evidence" wrapper so the
  // hover hint feels consistent across all containers in the modal.
  const evidenceTooltipProps = {
    label: 'Click for Evidence',
    hasArrow: true,
    placement: 'top',
    openDelay: 250,
    fontSize: 'xs',
    fontWeight: '600',
    bg: 'gray.900',
    color: 'white',
    borderRadius: 'md',
    px: 2.5,
    py: 1,
    hidden: demoMode,
  };
  
  // Business image state
  const [businessImage, setBusinessImage] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [competitorImages, setCompetitorImages] = useState({});

  // Traffic history state
  const [trafficHistory, setTrafficHistory] = useState([]);
  const [trafficLoading, setTrafficLoading] = useState(false);

  // Pricing perception state
  const [pricingData, setPricingData] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  // PDF export state
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfMode, setIsPdfMode] = useState(false);
  const printContentRef = useRef(null);

  // SWOT analysis state. Prefer scrape-populated `business.versus_insights`
  // when present; only call /ai/generate-business-insights when the field is
  // missing so the card still renders for every business.
  const [swotInsights, setSwotInsights] = useState(
    business?.versus_insights || ''
  );
  const [swotLoading, setSwotLoading] = useState(false);
  const [swotError, setSwotError] = useState(null);

  // SWOT "Click for Evidence" sub-modal — shows the actual high/low review
  // excerpts that the AI used as source material. Loaded lazily on click so
  // we don't pay the network cost for every modal open.
  const [swotEvidenceOpen, setSwotEvidenceOpen] = useState(false);
  const [swotEvidence, setSwotEvidence] = useState(null);
  const [swotEvidenceLoading, setSwotEvidenceLoading] = useState(false);
  const [swotEvidenceError, setSwotEvidenceError] = useState(null);

  const openSwotEvidence = async (e) => {
    if (e) e.stopPropagation();
    if (demoMode || !business?.maps_uri) return;

    setSwotEvidenceOpen(true);
    if (swotEvidence || swotEvidenceLoading) return;

    setSwotEvidenceLoading(true);
    setSwotEvidenceError(null);
    try {
      const data = await getSwotEvidence({ mapsUri: business.maps_uri });
      setSwotEvidence(data);
    } catch (err) {
      logger.error('Failed to load SWOT evidence:', err);
      setSwotEvidenceError(
        'Could not load the source reviews for this SWOT analysis right now.'
      );
    } finally {
      setSwotEvidenceLoading(false);
    }
  };

  const closeSwotEvidence = () => setSwotEvidenceOpen(false);

  const handleExportPdf = async () => {
    if (!printContentRef.current) return;
    setIsPdfExporting(true);

    try {
      setIsPdfMode(true);
      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });

      await generateScreenshotPdf(printContentRef.current, business?.name);
    } catch (err) {
      logger.error('[PDF_EXPORT] Failed to export PDF', err);
    } finally {
      setIsPdfMode(false);
      setIsPdfExporting(false);
    }
  };

  useEffect(() => {
    let frameId;

    const updateWidth = () => {
      if (!boxRef.current) return;
      setWidth(boxRef.current.offsetWidth);
    };

    const checkSize = () => {
      updateWidth();
      frameId = requestAnimationFrame(checkSize);
    };

    frameId = requestAnimationFrame(checkSize);

    window.addEventListener('resize', updateWidth);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateWidth);
    };
  }, []);
  
  // Fetch business image when modal opens or the viewed business changes
  useEffect(() => {
    let mounted = true;

    const fetchImage = async () => {
      if (!isOpen || !business?.maps_uri) {
        setBusinessImage(null);
        setImageLoaded(false);
        setImageError(false);
        return;
      }

      const demoImage =
        EXAMPLE_DEMO_IMAGES[business.maps_uri] || business.demo_image_url || null;

      if (demoMode) {
        if (mounted) {
          setBusinessImage(demoImage);
          setImageLoaded(false);
          setImageError(!demoImage);
        }
        return;
      }

      try {
        const images = await getImagesByBusiness(business.maps_uri);
        const imageUrl =
          (images && images.length > 0 && images[0].high_res_image_url_fetched) ||
          demoImage ||
          null;

        if (mounted) {
          setBusinessImage(imageUrl);
          setImageLoaded(false);
          setImageError(!imageUrl);
        }
      } catch (error) {
        logger.error('Error fetching business image:', error);
        if (mounted) {
          setBusinessImage(demoImage);
          setImageLoaded(false);
          setImageError(!demoImage);
        }
      }
    };

    fetchImage();

    return () => {
      mounted = false;
    };
  }, [isOpen, business?.maps_uri, business?.demo_image_url, demoMode]);

  // Fetch competitor thumbnails for the overview panel
  useEffect(() => {
    let mounted = true;

    const fetchCompetitorImages = async () => {
      if (!isOpen) {
        if (mounted) {
          setCompetitorImages({});
        }
        return;
      }

      if (demoMode) {
        if (mounted) {
          setCompetitorImages(EXAMPLE_DEMO_IMAGES);
        }
        return;
      }

      try {
        const images = await getBusinessImages();
        if (!mounted || !images) return;

        const imageMap = {};
        images.forEach((img) => {
          if (img.maps_uri && img.high_res_image_url_fetched) {
            imageMap[img.maps_uri] = img.high_res_image_url_fetched;
          }
        });
        setCompetitorImages(imageMap);
      } catch (error) {
        logger.error('Error fetching competitor images:', error);
        if (mounted) {
          setCompetitorImages({});
        }
      }
    };

    fetchCompetitorImages();

    return () => {
      mounted = false;
    };
  }, [isOpen, demoMode]);

  // Fetch historical traffic data
  useEffect(() => {
    let mounted = true;

    const fetchTrafficHistory = async () => {
      if (demoMode) {
        if (isOpen && business?.maps_uri && demoTrafficHistory) {
          setTrafficHistory(demoTrafficHistory[business.maps_uri] || []);
        }
        setTrafficLoading(false);
        return;
      }
      if (!isOpen || !business?.maps_uri) {
        setTrafficHistory([]);
        setTrafficLoading(false);
        return;
      }

      setTrafficLoading(true);
      try {
        const result = await getHistoricalTraffic();
        if (mounted && result?.businesses) {
          const businessData = result.businesses.find(
            b => b.mapsUri === business.maps_uri
          );
          if (businessData?.timeSeries) {
            const chartData = businessData.timeSeries
              .sort((a, b) => a.yearMonth.localeCompare(b.yearMonth))
              .slice(-24)
              .map(point => ({
                month: new Date(point.yearMonth + '-01T00:00:00').toLocaleDateString('en-US', {
                  month: 'short',
                  year: 'numeric'
                }),
                organicClicks: point.organicClicks || 0
              }));
            setTrafficHistory(chartData);
          }
        }
      } catch (error) {
        logger.error('Error fetching traffic history:', error);
      } finally {
        if (mounted) setTrafficLoading(false);
      }
    };

    fetchTrafficHistory();

    return () => { mounted = false; };
  }, [isOpen, business?.maps_uri, demoMode, demoTrafficHistory]);

  // Fetch pricing perception data
  useEffect(() => {
    let mounted = true;

    const fetchPricing = async () => {
      if (demoMode) {
        if (isOpen && business && demoPricingData) {
          setPricingData(typeof demoPricingData === 'function' ? demoPricingData(business) : demoPricingData);
        }
        setPricingLoading(false);
        return;
      }
      if (!isOpen || !business?.maps_uri) {
        setPricingData(null);
        setPricingLoading(false);
        return;
      }

      setPricingData(null);
      setPricingLoading(true);
      try {
        const data = await getBusinessPricing(business.maps_uri);
        if (mounted) {
          setPricingData(data);
        }
      } catch (error) {
        logger.error('Error fetching pricing data:', error);
        if (mounted) setPricingData(null);
      } finally {
        if (mounted) setPricingLoading(false);
      }
    };

    fetchPricing();

    return () => { mounted = false; };
  }, [isOpen, business?.maps_uri, business, demoMode, demoPricingData]);

  // Whenever the parent opens the modal for a different business, drop any
  // in-modal "peek at a competitor" override so the user always sees the
  // business they actually clicked on first.
  useEffect(() => {
    setSelectedCompetitor(null);
  }, [businessProp?.maps_uri]);

  // Drop cached SWOT evidence when switching businesses; hydrate SWOT content
  // from scrape-populated versus_insights when available, otherwise fetch via API.
  useEffect(() => {
    setSwotEvidence(null);
    setSwotEvidenceError(null);
    setSwotEvidenceOpen(false);
  }, [business?.maps_uri]);

  useEffect(() => {
    let mounted = true;

    const seeded = business?.versus_insights || '';
    setSwotInsights(seeded);
    setSwotError(null);

    if (!isOpen || demoMode) {
      setSwotLoading(false);
      return () => { mounted = false; };
    }
    if (seeded) {
      setSwotLoading(false);
      return () => { mounted = false; };
    }
    if (!business?.maps_uri) {
      setSwotLoading(false);
      return () => { mounted = false; };
    }

    setSwotLoading(true);
    (async () => {
      try {
        const result = await generateBusinessSwotInsights({
          mapsUri: business.maps_uri,
        });
        if (!mounted) return;
        setSwotInsights(result?.versus_insights || '');
        setSwotError(null);
      } catch (error) {
        logger.error('Failed to generate SWOT insights:', error);
        if (mounted) setSwotError('Failed to generate SWOT analysis.');
      } finally {
        if (mounted) setSwotLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [isOpen, business?.maps_uri, business?.versus_insights, demoMode]);

  // Theme colors - moved before any conditionals to fix React hooks rule violation
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  
  // Icon background colors for Business Details section
  const redIconBg = useColorModeValue('red.50', 'red.900');
  const redIconColor = useColorModeValue('red.500', 'red.300');
  const greenIconBg = useColorModeValue('green.50', 'green.900');
  const greenIconColor = useColorModeValue('green.500', 'green.300');
  const blueIconBg = useColorModeValue('blue.50', 'blue.900');
  const blueIconColor = useColorModeValue('blue.500', 'blue.300');
  const tealIconBg = useColorModeValue('teal.50', 'teal.900');  // eslint-disable-line no-unused-vars
  const tealIconColor = useColorModeValue('teal.500', 'teal.300');  // eslint-disable-line no-unused-vars
  const purpleIconBg = useColorModeValue('purple.50', 'purple.900');  // eslint-disable-line no-unused-vars
  const purpleIconColor = useColorModeValue('purple.500', 'purple.300');  // eslint-disable-line no-unused-vars
  const cyanIconBg = useColorModeValue('cyan.50', 'cyan.900');  // eslint-disable-line no-unused-vars
  const cyanIconColor = useColorModeValue('cyan.500', 'cyan.300');  // eslint-disable-line no-unused-vars
  const orangeIconBg = useColorModeValue('orange.50', 'orange.900');  // eslint-disable-line no-unused-vars
  const orangeIconColor = useColorModeValue('orange.500', 'orange.300');  // eslint-disable-line no-unused-vars
  
  // Hover and interactive colors
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const categoryBg = useColorModeValue('gray.50', 'gray.750');  // eslint-disable-line no-unused-vars
  const tagBg = useColorModeValue('white', 'gray.700');
  const tagHoverBorderColor = useColorModeValue('blue.200', 'blue.600');
  const tagHoverBg = useColorModeValue('blue.50', 'blue.900');
  
  // Card hover border colors (kept for dark mode compatibility)
  const blueHoverBorder = useColorModeValue('blue.200', 'blue.700');  // eslint-disable-line no-unused-vars
  const tealHoverBorder = useColorModeValue('teal.200', 'teal.700');  // eslint-disable-line no-unused-vars
  const purpleHoverBorder = useColorModeValue('purple.200', 'purple.700');  // eslint-disable-line no-unused-vars
  const greenHoverBorder = useColorModeValue('green.200', 'green.700');  // eslint-disable-line no-unused-vars
  const cyanHoverBorder = useColorModeValue('cyan.200', 'cyan.700');  // eslint-disable-line no-unused-vars
  const orangeHoverBorder = useColorModeValue('orange.200', 'orange.700');  // eslint-disable-line no-unused-vars

  // Colors for charts and visualizations
  const COLORS = {
    primary: '#0066ff',
    secondary: '#4299E1',
    success: '#48BB78',
    warning: '#ECC94B',
    danger: '#F56565',
    info: '#9F7AEA',
    charts: [
      '#2F80ED', // bright blue
      '#27AE60', // emerald green
      '#9B51E0', // purple
      '#F2994A', // orange
      '#EB5757', // bright red
      '#219EBC' // teal
    ]
  };

  // Process business data for the modal safely, handling both the business object and its properties
  const historicalData = business?.historicalData || {};
  const sentimentBreakdown = historicalData.sentimentBreakdown || {
    positive: 0,
    neutral: 0,
    negative: 0
  };

  // Prepare sentiment data for pie chart - moved before conditional return to fix hooks rule
  const sentimentPieData = useMemo(() => {
    return [
      {
        name: 'Positive 😊',
        value: sentimentBreakdown.positive,
        fill: '#48BB78'
      },
      {
        name: 'Neutral 😐',
        value: sentimentBreakdown.neutral,
        fill: '#ECC94B'
      },
      {
        name: 'Negative 😞',
        value: sentimentBreakdown.negative,
        fill: '#E53E3E'
      }
    ];
  }, [sentimentBreakdown]);

  // Handle favorite toggle (users can only have one favorite)
  const handleFavoriteClick = async (e) => {
    e.stopPropagation(); // Prevent triggering other clicks
    if (!business?.maps_uri) return;

    try {
      const result = await toggleFavorite(business.maps_uri);
      if (result.isNowFavorited) {
        toast({
          title: 'Great Choice! 🎉',
          description: 'Your favorite will now be ⭐ starred on all dashboards',
          status: 'success',
          duration: 3500,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Favorite removed',
          description: `${business.name} is no longer your favorite`,
          status: 'info',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      logger.error('Error toggling favorite:', error);
      toast({
        title: 'Error',
        description: 'Failed to update favorite status.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Safe early return after all hooks
  if (!business) return null;

  const sentimentRank =
    sortedBySentiment?.findIndex((b) => b.maps_uri === business.maps_uri) + 1 ||
    'N/A';
  const totalSentimentBusinesses = sortedBySentiment?.length || 'N/A';
  const growthRank =
    sortedByGrowth?.findIndex((b) => b.maps_uri === business.maps_uri) + 1 ||
    'N/A';
  const totalGrowthBusinesses = sortedByGrowth?.length || 'N/A';
  const totalReviewsRank =
    sortedByTotal?.findIndex((b) => b.maps_uri === business.maps_uri) + 1 ||
    'N/A';
  const totalTotalReviewsBusinesses = sortedByTotal?.length || 'N/A';
  const trafficRank =
    sortedByTraffic?.findIndex((b) => b.maps_uri === business.maps_uri) +
      1 || 'N/A';
  const totalTrafficBusinesses = sortedByTraffic?.length || 'N/A';
  const seoRank =
    sortedBySeo?.findIndex((b) => b.maps_uri === business.maps_uri) +
      1 || 'N/A';
  const totalSeoBusinesses = sortedBySeo?.length || 'N/A';

  const allBusinesses = sortedByTotal || sortedByGrowth || [];
  const totalScoreBusinesses = allBusinesses.length;

  // Score every business once using the same category-points formula as
  // the rank card. Sorting the result descending gives us both the rank
  // for the current business and the data we need to render the
  const scoredBusinesses = (() => {
    if (allBusinesses.length === 0) return [];
    const calcCatScore = (rank) => {
      if (!rank || rank === 'N/A' || rank <= 0) return 0;
      return Math.max(0, 21 - rank);
    };
    return allBusinesses
      .map((b) => {
        const gR =
          sortedByGrowth?.findIndex((g) => g.maps_uri === b.maps_uri) + 1 ||
          0;
        const tR =
          sortedByTotal?.findIndex((t) => t.maps_uri === b.maps_uri) + 1 ||
          0;
        const trR =
          sortedByTraffic?.findIndex((tr) => tr.maps_uri === b.maps_uri) +
            1 || 0;
        const sR =
          sortedBySentiment?.findIndex((s) => s.maps_uri === b.maps_uri) +
            1 || 0;
        const seR =
          sortedBySeo?.findIndex((se) => se.maps_uri === b.maps_uri) + 1 ||
          0;
        return {
          business: b,
          score:
            calcCatScore(gR) +
            calcCatScore(tR) +
            calcCatScore(trR) +
            calcCatScore(sR) +
            calcCatScore(seR),
        };
      })
      .sort((a, b) => b.score - a.score);
  })();

  const currentScoredIdx = scoredBusinesses.findIndex(
    (s) => s.business.maps_uri === business.maps_uri
  );
  const scoreRank = currentScoredIdx >= 0 ? currentScoredIdx + 1 : null;

  const handleSelectCompetitor = (competitorBusiness) => {
    if (!competitorBusiness?.maps_uri) return;
    if (onSelectBusiness) {
      onSelectBusiness(competitorBusiness);
      return;
    }
    setSelectedCompetitor(competitorBusiness);
  };

  // Get actual metric values for the current business from sorted arrays
  const businessGrowthData = sortedByGrowth?.find(b => b.maps_uri === business.maps_uri);
  const businessGrowthValue = businessGrowthData?.threeMonthGrowth ?? null;
  const avgGrowthValue = sortedByGrowth?.length > 0
    ? sortedByGrowth.reduce((sum, b) => sum + (b.threeMonthGrowth || 0), 0) / sortedByGrowth.length
    : null;

  const businessTotalReviews = business.rating_count || 0;
  const avgTotalReviews = sortedByTotal?.length > 0
    ? sortedByTotal.reduce((sum, b) => sum + (b.rating_count || 0), 0) / sortedByTotal.length
    : null;

  const businessTrafficData = sortedByTraffic?.find(b => b.maps_uri === business.maps_uri);
  const businessTrafficValue = businessTrafficData?.trafficValue ?? null;
  const avgTrafficValue = sortedByTraffic?.length > 0
    ? sortedByTraffic.reduce((sum, b) => sum + (b.trafficValue || 0), 0) / sortedByTraffic.length
    : null;

  const businessSentimentData = sortedBySentiment?.find(b => b.maps_uri === business.maps_uri);
  const businessSentimentValue = businessSentimentData?.sentimentScore ?? null;
  const avgSentimentValue = sortedBySentiment?.length > 0
    ? sortedBySentiment.reduce((sum, b) => sum + (b.sentimentScore || 0), 0) / sortedBySentiment.length
    : null;

  const businessSeoData = sortedBySeo?.find(b => b.maps_uri === business.maps_uri);
  const businessSeoValue = businessSeoData?.seoScore ?? null;
  // Average across the SEO ranking using each business's effective score
  // (real visitors/rank score when available, otherwise the estimate). This
  // keeps the comparison fair now that businesses without traffic data are
  // included in `sortedBySeo` via their estimated SEO score — using the
  // raw `seoScore` field would dilute the average with zeros.
  const avgSeoValue = sortedBySeo?.length > 0
    ? sortedBySeo.reduce(
        (sum, b) => sum + (b.seoScoreEstimated || b.seoScore || 0),
        0
      ) / sortedBySeo.length
    : null;

  const totalSentiment =
    sentimentBreakdown.positive +
    sentimentBreakdown.neutral +
    sentimentBreakdown.negative;
  const positivePercentage =
    totalSentiment > 0
      ? ((sentimentBreakdown.positive / totalSentiment) * 100).toFixed(1)
      : 0;

  const neutralPercentage =
    totalSentiment > 0
      ? ((sentimentBreakdown.neutral / totalSentiment) * 100).toFixed(1)
      : 0;

  const negativePercentage =
    totalSentiment > 0
      ? ((sentimentBreakdown.negative / totalSentiment) * 100).toFixed(1)
      : 0;

  // Calculate the competitor average review count per month (excluding the
  // current business) so we can overlay it on the Review Activity Trend chart.
  const competitorAverageByMonth = (() => {
    const competitors = (allBusinesses || []).filter(
      (b) => b && b.maps_uri !== business.maps_uri
    );
    if (competitors.length === 0) return {};

    const sums = {};
    const counts = {};
    competitors.forEach((comp) => {
      const compMonthly = comp.historicalData?.monthlyReviewCounts || {};
      Object.entries(compMonthly).forEach(([month, count]) => {
        sums[month] = (sums[month] || 0) + (count || 0);
        counts[month] = (counts[month] || 0) + 1;
      });
    });

    const averages = {};
    Object.keys(sums).forEach((month) => {
      averages[month] = counts[month] > 0 ? sums[month] / counts[month] : 0;
    });
    return averages;
  })();

  // Prepare monthly data
  const monthlyData = Object.entries(historicalData.monthlyReviewCounts || {})
    .map(([month, count]) => ({
      month: new Date(month + '-01T00:00:00').toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      }),
      count,
      competitorAvg:
        competitorAverageByMonth[month] !== undefined
          ? Math.round(competitorAverageByMonth[month] * 10) / 10
          : null,
      ...historicalData.monthlyRatings?.[month],
      originalMonth: month
    }))
    .sort((a, b) => new Date(a.originalMonth) - new Date(b.originalMonth));

  const hasCompetitorAverage =
    !hideCompetitorComparison &&
    monthlyData.some(
      (d) => d.competitorAvg !== null && d.competitorAvg !== undefined
    );

  // Historical average of this business's monthly review volume across the
  // displayed range. Rendered as a dotted reference line for context.
  const historicalAverageReviews = monthlyData.length
    ? monthlyData.reduce((sum, item) => sum + (item.count || 0), 0) /
      monthlyData.length
    : 0;
  const hasHistoricalAverage =
    monthlyData.length > 1 && historicalAverageReviews > 0;
  const historicalAverageDisplay =
    Math.round(historicalAverageReviews * 10) / 10;

  // Prepare cumulative reviews data for line chart
  // Calculate the total from historical data
  const historicalTotal = monthlyData.reduce((sum, item) => sum + (item.count || 0), 0);
  // Use business.rating_count as the actual total, calculate baseline for reviews before tracked period
  const actualTotal = business.rating_count || historicalTotal;
  const baselineReviews = Math.max(0, actualTotal - historicalTotal);
  
  let cumulative = baselineReviews;
  const cumulativeReviewsData = monthlyData.map(item => {
    cumulative += item.count || 0;
    return {
      month: item.month,
      cumulativeReviews: cumulative
    };
  });

  // Calculate monthly growth more accurately
  const months = Object.keys(historicalData.monthlyReviewCounts || {}).sort();
  
  // Get the last 30 days (approximated as most recent month)
  const lastMonth = months[months.length - 1];
  const lastMonthCount = historicalData.monthlyReviewCounts?.[lastMonth] || 0;
  
  // Calculate 3-month total for average
  const lastThreeMonths = months.slice(-3);
  const threeMonthGrowth = lastThreeMonths.reduce(
    (sum, month) => sum + (historicalData.monthlyReviewCounts?.[month] || 0),
    0
  );
  
  // Use the most recent month as "last 30 days" growth
  const monthlyGrowthRate = lastMonthCount; // Last month's actual count
  const averageMonthlyGrowth = threeMonthGrowth / Math.max(lastThreeMonths.length, 1);

  // Calculate growth comparison (last month vs previous month)
  let growthComparison = 0;
  let growthPercentage = 0;
  
  if (months.length >= 2) {
    const previousMonth = months[months.length - 2];
    const previousMonthCount = historicalData.monthlyReviewCounts?.[previousMonth] || 0;
    growthComparison = lastMonthCount - previousMonthCount;
    
    if (previousMonthCount > 0) {
      growthPercentage = ((lastMonthCount - previousMonthCount) / previousMonthCount * 100).toFixed(1);
    } else if (lastMonthCount > 0) {
      growthPercentage = "100";
    } else {
      growthPercentage = "0";
    }
  }

  // Inline mode: render the entire modal content as a plain inline card so
  // marketing surfaces (e.g. /agencies hero) can embed the *actual* business
  // review module, not a recreation. The internal layout is otherwise
  // pixel-identical to the in-app modal.
  const ContainerWrap = inline ? React.Fragment : Modal;
  const containerWrapProps = inline ? {} : { isOpen, onClose, size: '6xl' };
  const ContentWrap = inline ? Box : ModalContent;
  const contentWrapProps = inline
    ? flush
      ? {
          w: '100%',
          overflow: 'hidden',
          bg: cardBg,
        }
      : {
          w: '100%',
          overflow: 'hidden',
          borderRadius: '2xl',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
          bg: cardBg,
          border: '1px solid',
          borderColor: 'gray.100',
        }
    : {
        maxW: compact ? '68vw' : '90vw',
        maxH: compact ? '78vh' : '90vh',
        h: compact ? '78vh' : '90vh',
        mx: 'auto',
        my: compact ? '11vh' : '5vh',
        overflow: 'hidden',
        borderRadius: '2xl',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
        bg: cardBg,
        border: '1px solid',
        borderColor: 'gray.100',
      };
  const HeaderWrap = inline ? Box : ModalHeader;

  return (
    <ContainerWrap {...containerWrapProps}>
      {!inline && (
        <ModalOverlay
          bg='blackAlpha.400'
          backdropFilter='blur(12px)'
        />
      )}
      <ContentWrap {...contentWrapProps}>
        <Box
          ref={printContentRef}
          data-pdf-root
          h={inline ? 'auto' : 'full'}
          display='flex'
          flexDirection='column'
        >
          <HeaderWrap
            borderBottom='1px solid'
            borderColor='gray.100'
            flex='0 0 auto'
            px={inline ? 4 : 8}
            py={inline ? 3 : 5}
            bg='white'
          >
            <HStack
              align='center'
              spacing={5}
            >
              {/* Business Image */}
              {businessImage && !imageError && (
                <Box
                  position="relative"
                  w="72px"
                  h="72px"
                  borderRadius="16px"
                  overflow="hidden"
                  flexShrink={0}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.100"
                >
                  {!imageLoaded && (
                    <Skeleton
                      position="absolute"
                      top={0}
                      left={0}
                      w="100%"
                      h="100%"
                      startColor="gray.100"
                      endColor="gray.300"
                    />
                  )}
                  <Image
                    src={businessImage}
                    alt={business.name}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    loading="lazy"
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageError(true)}
                    opacity={imageLoaded ? 1 : 0}
                    transition="opacity 0.3s ease-in-out"
                  />
                </Box>
              )}
              
              <VStack
                align='stretch'
                spacing={1}
                flex={1}
              >
                <Flex
                  justify='space-between'
                  align='center'
                  wrap='nowrap'
                >
                  <HStack spacing={3} align='center' flexWrap="wrap">
                    <Heading
                      as='h2'
                      fontSize='3xl'
                      fontWeight='600'
                      color='gray.900'
                      lineHeight='1.2'
                      noOfLines={1}
                      letterSpacing='-0.02em'
                    >
                      {business.name}
                    </Heading>
                    <IconButton
                      icon={<Icon as={isFavorited(business.maps_uri) ? FaStar : FaRegStar} boxSize={5} />}
                      size='md'
                      variant='ghost'
                      color={isFavorited(business.maps_uri) ? 'yellow.400' : 'gray.400'}
                      onClick={handleFavoriteClick}
                      aria-label={isFavorited(business.maps_uri) ? 'Remove from favorites' : 'Add to favorites'}
                      _hover={{
                        color: 'yellow.400',
                        transform: 'scale(1.1)',
                        bg: 'yellow.50'
                      }}
                      transition='all 0.2s'
                    />
                    <IconButton
                      icon={
                        isPdfExporting
                          ? <Spinner size='xs' color='red.400' />
                          : <Icon as={FaFilePdf} boxSize={4} />
                      }
                      size='sm'
                      variant='ghost'
                      borderRadius='full'
                      bg='gray.50'
                      color='red.400'
                      border='1px solid'
                      borderColor='gray.100'
                      aria-label='Export as PDF'
                      title='Export as PDF'
                      isLoading={isPdfExporting}
                      onClick={handleExportPdf}
                      _hover={{
                        bg: 'red.50',
                        color: 'red.500',
                        borderColor: 'red.200',
                      }}
                      transition='all 0.15s ease'
                    />
                  </HStack>
                  <HStack
                    spacing={3}
                    align='center'
                    mr={12}
                    flexWrap="wrap"
                  >
                    <Badge
                      bg='gray.50'
                      color='gray.600'
                      px={3}
                      py={1}
                      borderRadius='full'
                      fontSize='xs'
                      fontWeight='500'
                      textTransform='capitalize'
                      letterSpacing='normal'
                      border='1px solid'
                      borderColor='gray.200'
                    >
                      {business.types?.[0]
                        .split('_')
                        .map(
                          (word) => word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(' ')}
                    </Badge>
                    <HStack
                      spacing={1.5}
                      bg='white'
                      px={3}
                      py={1.5}
                      borderRadius='full'
                      align='center'
                      border='1px solid'
                      borderColor='gray.200'
                    >
                      <Icon
                        as={FaStar}
                        color='yellow.400'
                        boxSize={4}
                      />
                      <Text
                        fontSize='md'
                        fontWeight='600'
                        color='gray.800'
                        as={'span'}
                      >
                        {business.rating_avg?.toFixed(1)}
                      </Text>
                      <Text
                        fontSize='xs'
                        fontWeight='400'
                        color='gray.400'
                        as={'span'}
                      >
                        ({business.rating_count?.toLocaleString() || 0})
                      </Text>
                    </HStack>
                  </HStack>
                </Flex>
              </VStack>
            </HStack>
            {!inline && (
              <ModalCloseButton
                size='md'
                top={4}
                right={4}
                borderRadius='full'
                bg='gray.50'
                color='gray.400'
                border='1px solid'
                borderColor='gray.100'
                _hover={{
                  bg: 'gray.100',
                  color: 'gray.600'
                }}
                transition='all 0.15s ease'
              />
            )}
          </HeaderWrap>

          <Box
            data-pdf-body
            flex={inline ? '0 0 auto' : '1 1 auto'}
            overflow={inline ? 'visible' : 'auto'}
            p={inline ? 0 : 8}
            bgColor='gray.50'
            sx={{
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '3px' },
              '&::-webkit-scrollbar-thumb:hover': { background: '#A0AEC0' },
            }}
          >
            <Box mb={5}>
              <Box
                display='grid'
                gridTemplateColumns='repeat(5, 1fr)'
                gap='16px'
              >
                {/* Growth Ranking Card */}
                <ChakraTooltip {...evidenceTooltipProps}>
                <Box
                  position='relative'
                  overflow='hidden'
                  display='flex'
                  flexDirection='column'
                  bg='white'
                  borderRadius='xl'
                  cursor='pointer'
                  onClick={() => navigateToDashboard('/dashboards/gross-growth')}
                  boxShadow={
                    growthRank !== 'N/A' && totalGrowthBusinesses !== 'N/A' && growthRank <= 5
                      ? '0 8px 24px -8px rgba(45, 106, 79, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.05)'
                      : 'lg'
                  }
                  transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  border='1px solid'
                  borderColor={
                    growthRank !== 'N/A' && totalGrowthBusinesses !== 'N/A'
                      ? growthRank <= 5
                        ? 'green.200'
                        : growthRank > totalGrowthBusinesses - 5
                          ? 'red.200'
                          : 'gray.100'
                      : 'gray.100'
                  }
                  _hover={{ 
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px -10px rgba(0, 0, 0, 0.18), 0 6px 12px -4px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  {/* Top gradient accent bar */}
                  <Box
                    h='3px'
                    bgGradient={
                      growthRank !== 'N/A' && totalGrowthBusinesses !== 'N/A'
                        ? growthRank <= 5
                          ? 'linear(to-r, green.400, green.500, green.400)'
                          : growthRank > totalGrowthBusinesses - 5
                            ? 'linear(to-r, red.300, red.400, red.300)'
                            : 'linear(to-r, gray.200, gray.300, gray.200)'
                        : 'linear(to-r, gray.200, gray.300, gray.200)'
                    }
                  />
                  {growthRank !== 'N/A' && totalGrowthBusinesses !== 'N/A' && (
                    (growthRank <= 5 || growthRank > totalGrowthBusinesses - 5) && (
                      <GiftRibbon 
                        isTop5={growthRank <= 5} 
                        label={growthRank <= 5 ? 'Top 5' : 'Btm 5'} 
                      />
                    )
                  )}
                  <Box
                    px={3}
                    py={1}
                    bg={
                      growthRank !== 'N/A' && totalGrowthBusinesses !== 'N/A'
                        ? growthRank <= 5
                          ? 'linear-gradient(180deg, rgba(240,253,244,0.8) 0%, rgba(255,255,255,1) 100%)'
                          : growthRank > totalGrowthBusinesses - 5
                            ? 'linear-gradient(180deg, rgba(254,242,242,0.8) 0%, rgba(255,255,255,1) 100%)'
                            : 'white'
                        : 'white'
                    }
                    flex='1'
                  >
                    <Flex align='center' gap='8px' mb='1px'>
                      <Flex
                        w='28px'
                        h='28px'
                        align='center'
                        justify='center'
                        borderRadius='md'
                        bg={growthRank !== 'N/A' && growthRank <= 5 ? 'green.100' : 'gray.100'}
                      >
                        <Icon 
                          as={HiOutlineTrendingUp} 
                          boxSize='14px' 
                          color={growthRank !== 'N/A' && growthRank <= 5 ? 'green.600' : 'gray.500'}
                        />
                      </Flex>
                      <Text mb={0}
                        fontSize='14px'
                        fontWeight='600'
                        color='gray.600'
                        textTransform='uppercase'
                        letterSpacing='0.5px'
                      >
                        Review Growth
                      </Text>
                    </Flex>
                    <Flex align='baseline' justify='center' py='2px'>
                      <Text
                        fontSize='52px'
                        fontWeight='800'
                        fontStyle='italic'
                        color='gray.800'
                        lineHeight='1'
                        letterSpacing='-2px'
                      >
                        {getOrdinal(growthRank)}
                      </Text>
                    </Flex>
                    <Box textAlign='center'>
                      <Text
                        fontSize='14px'
                        fontWeight='800'
                        color={getCategoryPoints(growthRank) >= 15 ? 'green.500' : getCategoryPoints(growthRank) >= 10 ? 'blue.500' : getCategoryPoints(growthRank) >= 5 ? 'orange.500' : 'gray.500'}
                        lineHeight='1.2'
                        fontFamily="'Georgia', serif"
                      >
                        {businessGrowthValue !== null ? `${businessGrowthValue >= 0 ? '+' : ''}${Math.round(businessGrowthValue)}` : 'N/A'}
                      </Text>
                      <Text
                        fontSize='9px'
                        fontWeight='500'
                        color='gray.400'
                      >
                        {avgGrowthValue !== null ? `avg ${avgGrowthValue >= 0 ? '+' : ''}${Math.round(avgGrowthValue)}` : ''}
                      </Text>
                    </Box>
                  </Box>
                </Box>
                </ChakraTooltip>

                {/* Total Reviews Ranking Card */}
                <ChakraTooltip {...evidenceTooltipProps}>
                <Box
                  position='relative'
                  overflow='hidden'
                  display='flex'
                  flexDirection='column'
                  bg='white'
                  borderRadius='xl'
                  cursor='pointer'
                  onClick={() => navigateToDashboard('/dashboards/total-reviews')}
                  boxShadow={
                    totalReviewsRank !== 'N/A' && totalTotalReviewsBusinesses !== 'N/A' && totalReviewsRank <= 5
                      ? '0 8px 24px -8px rgba(45, 106, 79, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.05)'
                      : 'lg'
                  }
                  transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  border='1px solid'
                  borderColor={
                    totalReviewsRank !== 'N/A' && totalTotalReviewsBusinesses !== 'N/A'
                      ? totalReviewsRank <= 5
                        ? 'green.200'
                        : totalReviewsRank > totalTotalReviewsBusinesses - 5
                          ? 'red.200'
                          : 'gray.100'
                      : 'gray.100'
                  }
                  _hover={{ 
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px -10px rgba(0, 0, 0, 0.18), 0 6px 12px -4px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  {/* Top gradient accent bar */}
                  <Box
                    h='3px'
                    bgGradient={
                      totalReviewsRank !== 'N/A' && totalTotalReviewsBusinesses !== 'N/A'
                        ? totalReviewsRank <= 5
                          ? 'linear(to-r, green.400, green.500, green.400)'
                          : totalReviewsRank > totalTotalReviewsBusinesses - 5
                            ? 'linear(to-r, red.300, red.400, red.300)'
                            : 'linear(to-r, gray.200, gray.300, gray.200)'
                        : 'linear(to-r, gray.200, gray.300, gray.200)'
                    }
                  />
                  {totalReviewsRank !== 'N/A' && totalTotalReviewsBusinesses !== 'N/A' && (
                    (totalReviewsRank <= 5 || totalReviewsRank > totalTotalReviewsBusinesses - 5) && (
                      <GiftRibbon 
                        isTop5={totalReviewsRank <= 5} 
                        label={totalReviewsRank <= 5 ? 'Top 5' : 'Btm 5'} 
                      />
                    )
                  )}
                  <Box
                    px={3}
                    py={1}
                    bg={
                      totalReviewsRank !== 'N/A' && totalTotalReviewsBusinesses !== 'N/A'
                        ? totalReviewsRank <= 5
                          ? 'linear-gradient(180deg, rgba(240,253,244,0.8) 0%, rgba(255,255,255,1) 100%)'
                          : totalReviewsRank > totalTotalReviewsBusinesses - 5
                            ? 'linear-gradient(180deg, rgba(254,242,242,0.8) 0%, rgba(255,255,255,1) 100%)'
                            : 'white'
                        : 'white'
                    }
                    flex='1'
                  >
                    <Flex align='center' gap='8px' mb='1px'>
                      <Flex
                        w='28px'
                        h='28px'
                        align='center'
                        justify='center'
                        borderRadius='md'
                        bg={totalReviewsRank !== 'N/A' && totalReviewsRank <= 5 ? 'green.100' : 'gray.100'}
                      >
                        <Icon 
                          as={HiOutlineHashtag} 
                          boxSize='14px' 
                          color={totalReviewsRank !== 'N/A' && totalReviewsRank <= 5 ? 'green.600' : 'gray.500'}
                        />
                      </Flex>
                      <Text mb={0}
                        fontSize='14px'
                        fontWeight='600'
                        color='gray.600'
                        textTransform='uppercase'
                        letterSpacing='0.5px'
                      >
                        Total Reviews
                      </Text>
                    </Flex>
                    <Flex align='baseline' justify='center' py='2px'>
                      <Text
                        fontSize='52px'
                        fontWeight='800'
                        fontStyle='italic'
                        color='gray.800'
                        lineHeight='1'
                        letterSpacing='-2px'
                      >
                        {getOrdinal(totalReviewsRank)}
                      </Text>
                    </Flex>
                    <Box textAlign='center'>
                      <Text
                        fontSize='14px'
                        fontWeight='800'
                        color={getCategoryPoints(totalReviewsRank) >= 15 ? 'green.500' : getCategoryPoints(totalReviewsRank) >= 10 ? 'blue.500' : getCategoryPoints(totalReviewsRank) >= 5 ? 'orange.500' : 'gray.500'}
                        lineHeight='1.2'
                        fontFamily="'Georgia', serif"
                      >
                        {businessTotalReviews.toLocaleString()}
                      </Text>
                      <Text
                        fontSize='9px'
                        fontWeight='500'
                        color='gray.400'
                      >
                        {avgTotalReviews !== null ? `avg ${Math.round(avgTotalReviews).toLocaleString()}` : ''}
                      </Text>
                    </Box>
                  </Box>
                </Box>
                </ChakraTooltip>

                {/* Website Traffic Ranking Card */}
                <ChakraTooltip {...evidenceTooltipProps}>
                <Box
                  position='relative'
                  overflow='hidden'
                  display='flex'
                  flexDirection='column'
                  bg='white'
                  borderRadius='xl'
                  cursor='pointer'
                  onClick={() => navigateToDashboard('/dashboards/keyword-intelligence')}
                  boxShadow={
                    trafficRank !== 'N/A' && totalTrafficBusinesses !== 'N/A' && trafficRank <= 5
                      ? '0 8px 24px -8px rgba(45, 106, 79, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.05)'
                      : 'lg'
                  }
                  transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  border='1px solid'
                  borderColor={
                    trafficRank !== 'N/A' && totalTrafficBusinesses !== 'N/A'
                      ? trafficRank <= 5
                        ? 'green.200'
                        : trafficRank > totalTrafficBusinesses - 5
                          ? 'red.200'
                          : 'gray.100'
                      : 'gray.100'
                  }
                  _hover={{ 
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px -10px rgba(0, 0, 0, 0.18), 0 6px 12px -4px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  {/* Top gradient accent bar */}
                  <Box
                    h='3px'
                    bgGradient={
                      trafficRank !== 'N/A' && totalTrafficBusinesses !== 'N/A'
                        ? trafficRank <= 5
                          ? 'linear(to-r, green.400, green.500, green.400)'
                          : trafficRank > totalTrafficBusinesses - 5
                            ? 'linear(to-r, red.300, red.400, red.300)'
                            : 'linear(to-r, gray.200, gray.300, gray.200)'
                        : 'linear(to-r, gray.200, gray.300, gray.200)'
                    }
                  />
                  {trafficRank !== 'N/A' && totalTrafficBusinesses !== 'N/A' && (
                    (trafficRank <= 5 || trafficRank > totalTrafficBusinesses - 5) && (
                      <GiftRibbon 
                        isTop5={trafficRank <= 5} 
                        label={trafficRank <= 5 ? 'Top 5' : 'Btm 5'} 
                      />
                    )
                  )}
                  <Box
                    px={3}
                    py={1}
                    bg={
                      trafficRank !== 'N/A' && totalTrafficBusinesses !== 'N/A'
                        ? trafficRank <= 5
                          ? 'linear-gradient(180deg, rgba(240,253,244,0.8) 0%, rgba(255,255,255,1) 100%)'
                          : trafficRank > totalTrafficBusinesses - 5
                            ? 'linear-gradient(180deg, rgba(254,242,242,0.8) 0%, rgba(255,255,255,1) 100%)'
                            : 'white'
                        : 'white'
                    }
                    flex='1'
                  >
                    <Flex align='center' gap='8px' mb='1px'>
                      <Flex
                        w='28px'
                        h='28px'
                        align='center'
                        justify='center'
                        borderRadius='md'
                        bg={trafficRank !== 'N/A' && trafficRank <= 5 ? 'green.100' : 'gray.100'}
                      >
                        <Icon 
                          as={FaGlobe} 
                          boxSize='14px' 
                          color={trafficRank !== 'N/A' && trafficRank <= 5 ? 'green.600' : 'gray.500'}
                        />
                      </Flex>
                      <Text mb={0}
                        fontSize='14px'
                        fontWeight='600'
                        color='gray.600'
                        textTransform='uppercase'
                        letterSpacing='0.5px'
                      >
                        Website Traffic
                      </Text>
                    </Flex>
                    <Flex align='baseline' justify='center' py='2px'>
                      <Text
                        fontSize='52px'
                        fontWeight='800'
                        fontStyle='italic'
                        color='gray.800'
                        lineHeight='1'
                        letterSpacing='-2px'
                      >
                        {getOrdinal(trafficRank)}
                      </Text>
                    </Flex>
                    <Box textAlign='center'>
                      <Text
                        fontSize='14px'
                        fontWeight='800'
                        color={getCategoryPoints(trafficRank) >= 15 ? 'green.500' : getCategoryPoints(trafficRank) >= 10 ? 'blue.500' : getCategoryPoints(trafficRank) >= 5 ? 'orange.500' : 'gray.500'}
                        lineHeight='1.2'
                        fontFamily="'Georgia', serif"
                      >
                        {businessTrafficValue !== null ? businessTrafficValue.toLocaleString() : 'N/A'}
                      </Text>
                      <Text
                        fontSize='9px'
                        fontWeight='500'
                        color='gray.400'
                      >
                        {avgTrafficValue !== null ? `avg ${Math.round(avgTrafficValue).toLocaleString()}` : ''}
                      </Text>
                    </Box>
                  </Box>
                </Box>
                </ChakraTooltip>

                {/* Sentiment Ranking Card */}
                <ChakraTooltip {...evidenceTooltipProps}>
                <Box
                  position='relative'
                  overflow='hidden'
                  display='flex'
                  flexDirection='column'
                  bg='white'
                  borderRadius='xl'
                  cursor='pointer'
                  onClick={() => navigateToDashboard('/dashboards/sentiment')}
                  boxShadow={
                    sentimentRank !== 'N/A' && totalSentimentBusinesses !== 'N/A' && sentimentRank <= 5
                      ? '0 8px 24px -8px rgba(45, 106, 79, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.05)'
                      : sentimentRank !== 'N/A' && totalSentimentBusinesses !== 'N/A' && sentimentRank > totalSentimentBusinesses - 5
                        ? '0 8px 24px -8px rgba(197, 48, 48, 0.25), 0 2px 4px -1px rgba(0, 0, 0, 0.05)'
                        : 'lg'
                  }
                  transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  border='1px solid'
                  borderColor={
                    sentimentRank !== 'N/A' && totalSentimentBusinesses !== 'N/A'
                      ? sentimentRank <= 5
                        ? 'green.200'
                        : sentimentRank > totalSentimentBusinesses - 5
                          ? 'red.200'
                          : 'gray.100'
                      : 'gray.100'
                  }
                  _hover={{ 
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px -10px rgba(0, 0, 0, 0.18), 0 6px 12px -4px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  {/* Top gradient accent bar */}
                  <Box
                    h='3px'
                    bgGradient={
                      sentimentRank !== 'N/A' && totalSentimentBusinesses !== 'N/A'
                        ? sentimentRank <= 5
                          ? 'linear(to-r, green.400, green.500, green.400)'
                          : sentimentRank > totalSentimentBusinesses - 5
                            ? 'linear(to-r, red.300, red.400, red.300)'
                            : 'linear(to-r, gray.200, gray.300, gray.200)'
                        : 'linear(to-r, gray.200, gray.300, gray.200)'
                    }
                  />
                  {sentimentRank !== 'N/A' && totalSentimentBusinesses !== 'N/A' && (
                    (sentimentRank <= 5 || sentimentRank > totalSentimentBusinesses - 5) && (
                      <GiftRibbon 
                        isTop5={sentimentRank <= 5} 
                        label={sentimentRank <= 5 ? 'Top 5' : 'Btm 5'} 
                      />
                    )
                  )}
                  <Box
                    px={3}
                    py={1}
                    bg={
                      sentimentRank !== 'N/A' && totalSentimentBusinesses !== 'N/A'
                        ? sentimentRank <= 5
                          ? 'linear-gradient(180deg, rgba(240,253,244,0.8) 0%, rgba(255,255,255,1) 100%)'
                          : sentimentRank > totalSentimentBusinesses - 5
                            ? 'linear-gradient(180deg, rgba(254,242,242,0.8) 0%, rgba(255,255,255,1) 100%)'
                            : 'white'
                        : 'white'
                    }
                    flex='1'
                  >
                    <Flex align='center' gap='8px' mb='1px'>
                      <Flex
                        w='28px'
                        h='28px'
                        align='center'
                        justify='center'
                        borderRadius='md'
                        bg={
                          sentimentRank !== 'N/A' && sentimentRank <= 5 
                            ? 'green.100' 
                            : sentimentRank !== 'N/A' && totalSentimentBusinesses !== 'N/A' && sentimentRank > totalSentimentBusinesses - 5
                              ? 'red.100'
                              : 'gray.100'
                        }
                      >
                        <Icon 
                          as={HiOutlineEmojiHappy} 
                          boxSize='14px' 
                          color={
                            sentimentRank !== 'N/A' && sentimentRank <= 5 
                              ? 'green.600' 
                              : sentimentRank !== 'N/A' && totalSentimentBusinesses !== 'N/A' && sentimentRank > totalSentimentBusinesses - 5
                                ? 'red.500'
                                : 'gray.500'
                          }
                        />
                      </Flex>
                      <Text mb={0}
                        fontSize='14px'
                        fontWeight='600'
                        color='gray.600'
                        textTransform='uppercase'
                        letterSpacing='0.5px'
                      >
                        Sentiment
                      </Text>
                    </Flex>
                    <Flex align='baseline' justify='center' py='2px'>
                      <Text
                        fontSize='52px'
                        fontWeight='800'
                        fontStyle='italic'
                        color='gray.800'
                        lineHeight='1'
                        letterSpacing='-2px'
                      >
                        {getOrdinal(sentimentRank)}
                      </Text>
                    </Flex>
                    <Box textAlign='center'>
                      <Text
                        fontSize='14px'
                        fontWeight='800'
                        color={getCategoryPoints(sentimentRank) >= 15 ? 'green.500' : getCategoryPoints(sentimentRank) >= 10 ? 'blue.500' : getCategoryPoints(sentimentRank) >= 5 ? 'orange.500' : 'gray.500'}
                        lineHeight='1.2'
                        fontFamily="'Georgia', serif"
                      >
                        {businessSentimentValue !== null ? `${businessSentimentValue.toFixed(1)}%` : 'N/A'}
                      </Text>
                      <Text
                        fontSize='9px'
                        fontWeight='500'
                        color='gray.400'
                      >
                        {avgSentimentValue !== null ? `avg ${avgSentimentValue.toFixed(1)}%` : ''}
                      </Text>
                    </Box>
                  </Box>
                </Box>
                </ChakraTooltip>

                {/* SEO Score Ranking Card */}
                <ChakraTooltip {...evidenceTooltipProps}>
                <Box
                  position='relative'
                  overflow='hidden'
                  display='flex'
                  flexDirection='column'
                  bg='white'
                  borderRadius='xl'
                  cursor='pointer'
                  onClick={() => navigateToDashboard('/dashboards/web-traffic')}
                  boxShadow={
                    seoRank !== 'N/A' && totalSeoBusinesses !== 'N/A' && seoRank <= 5
                      ? '0 8px 24px -8px rgba(45, 106, 79, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.05)'
                      : 'lg'
                  }
                  transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  border='1px solid'
                  borderColor={
                    seoRank !== 'N/A' && totalSeoBusinesses !== 'N/A'
                      ? seoRank <= 5
                        ? 'green.200'
                        : seoRank > totalSeoBusinesses - 5
                          ? 'red.200'
                          : 'gray.100'
                      : 'gray.100'
                  }
                  _hover={{ 
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px -10px rgba(0, 0, 0, 0.18), 0 6px 12px -4px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  {/* Top gradient accent bar */}
                  <Box
                    h='3px'
                    bgGradient={
                      seoRank !== 'N/A' && totalSeoBusinesses !== 'N/A'
                        ? seoRank <= 5
                          ? 'linear(to-r, green.400, green.500, green.400)'
                          : seoRank > totalSeoBusinesses - 5
                            ? 'linear(to-r, red.300, red.400, red.300)'
                            : 'linear(to-r, gray.200, gray.300, gray.200)'
                        : 'linear(to-r, gray.200, gray.300, gray.200)'
                    }
                  />
                  {seoRank !== 'N/A' && totalSeoBusinesses !== 'N/A' && (
                    (seoRank <= 5 || seoRank > totalSeoBusinesses - 5) && (
                      <GiftRibbon 
                        isTop5={seoRank <= 5} 
                        label={seoRank <= 5 ? 'Top 5' : 'Btm 5'} 
                      />
                    )
                  )}
                  <Box
                    px={3}
                    py={1}
                    bg={
                      seoRank !== 'N/A' && totalSeoBusinesses !== 'N/A'
                        ? seoRank <= 5
                          ? 'linear-gradient(180deg, rgba(240,253,244,0.8) 0%, rgba(255,255,255,1) 100%)'
                          : seoRank > totalSeoBusinesses - 5
                            ? 'linear-gradient(180deg, rgba(254,242,242,0.8) 0%, rgba(255,255,255,1) 100%)'
                            : 'white'
                        : 'white'
                    }
                    flex='1'
                  >
                    <Flex align='center' gap='8px' mb='1px'>
                      <Flex
                        w='28px'
                        h='28px'
                        align='center'
                        justify='center'
                        borderRadius='md'
                        bg={seoRank !== 'N/A' && seoRank <= 5 ? 'green.100' : 'gray.100'}
                      >
                        <Icon 
                          as={FaSearch} 
                          boxSize='14px' 
                          color={seoRank !== 'N/A' && seoRank <= 5 ? 'green.600' : 'gray.500'}
                        />
                      </Flex>
                      <Text mb={0}
                        fontSize='14px'
                        fontWeight='600'
                        color='gray.600'
                        textTransform='uppercase'
                        letterSpacing='0.5px'
                      >
                        SEO Score
                      </Text>
                    </Flex>
                    <Flex align='baseline' justify='center' py='2px'>
                      <Text
                        fontSize='52px'
                        fontWeight='800'
                        fontStyle='italic'
                        color='gray.800'
                        lineHeight='1'
                        letterSpacing='-2px'
                      >
                        {getOrdinal(seoRank)}
                      </Text>
                    </Flex>
                    <Box textAlign='center'>
                      <Text
                        fontSize='14px'
                        fontWeight='800'
                        color={getCategoryPoints(seoRank) >= 15 ? 'green.500' : getCategoryPoints(seoRank) >= 10 ? 'blue.500' : getCategoryPoints(seoRank) >= 5 ? 'orange.500' : 'gray.500'}
                        lineHeight='1.2'
                        fontFamily="'Georgia', serif"
                      >
                        {businessSeoValue !== null && businessSeoValue > 0
                          ? Math.round(businessSeoValue).toLocaleString()
                          : `~${Math.round(estimateSeoScore(business)).toLocaleString()}`}
                      </Text>
                      <Text
                        fontSize='9px'
                        fontWeight='500'
                        color='gray.400'
                      >
                        {avgSeoValue !== null ? `avg ${Math.round(avgSeoValue).toLocaleString()}` : ''}
                      </Text>
                    </Box>
                  </Box>
                </Box>
                </ChakraTooltip>
              </Box>
            </Box>

            {/* Full-width Localytics Score breakdown, followed by the
                two-up acquisition/retention row ("Get More Customers" +
                "Keep More Customers"). The score card is full width now,
                so the two panels sit below it at 50/50. */}
            {(() => {
              const immediateActionMarket =
                (sortedByTotal && sortedByTotal.length > 0 && sortedByTotal) ||
                (sortedByGrowth && sortedByGrowth.length > 0 && sortedByGrowth) ||
                (sortedByTraffic && sortedByTraffic.length > 0 && sortedByTraffic) ||
                (sortedBySentiment && sortedBySentiment.length > 0 && sortedBySentiment) ||
                [];

              // Ad-running context for the PPC play.
              const adRankingForAction = getBusinessAdRank(
                business,
                immediateActionMarket,
              );
              const rankedByAds = rankBusinessesByAdCount(immediateActionMarket);
              const topAdvertiserEntry =
                rankedByAds.find(
                  (entry) =>
                    entry.mapsUri !== business.maps_uri && entry.adCount > 0
                ) || null;
              const adDataForGrowth = {
                runningAds: (adRankingForAction.adCount || 0) > 0,
                adCount: adRankingForAction.adCount || 0,
                competitorsAdvertising: !!topAdvertiserEntry,
                topAdvertiserName: topAdvertiserEntry?.name || null,
                topAdvertiserCount: topAdvertiserEntry?.adCount || null,
                avgAdsMarket: adRankingForAction.avgAdsAcrossMarket || 0,
              };

              // Review-velocity comparison vs. the #1 competitor (top of
              // the review-growth leaderboard, excluding this business).
              const reviewLeaderEntry =
                (sortedByGrowth || []).find(
                  (b) => b && b.maps_uri !== business.maps_uri
                ) || null;
              const reviewsForGrowth = {
                self: businessGrowthValue,
                competitorName: reviewLeaderEntry?.name || null,
                competitorValue:
                  reviewLeaderEntry?.threeMonthGrowth ?? null,
              };

              // Website-traffic comparison vs. the #1 traffic competitor.
              const trafficLeaderEntry =
                (sortedByTraffic || []).find(
                  (b) => b && b.maps_uri !== business.maps_uri
                ) || null;
              const trafficForGrowth = {
                self: businessTrafficValue,
                competitorName: trafficLeaderEntry?.name || null,
                competitorValue: trafficLeaderEntry?.trafficValue ?? null,
              };

              const positiveThemesForRetention = Array.isArray(
                business.common_positive_themes
              )
                ? business.common_positive_themes.slice(0, 5)
                : [];
              const negativeThemesForRetention = Array.isArray(
                business.common_negative_themes
              )
                ? business.common_negative_themes.slice(0, 5)
                : [];

              return (
                <>
                  <ChakraTooltip {...evidenceTooltipProps}>
                    <Box
                      mb={5}
                      cursor='pointer'
                      onClick={() =>
                        navigateToDashboard('/dashboards/localytics-score')
                      }
                    >
                      <LocalyticsRankingCard
                        growthRank={growthRank}
                        totalGrowthBusinesses={totalGrowthBusinesses}
                        totalReviewsRank={totalReviewsRank}
                        totalReviewsBusinesses={totalTotalReviewsBusinesses}
                        trafficRank={trafficRank}
                        totalTrafficBusinesses={totalTrafficBusinesses}
                        sentimentRank={sentimentRank}
                        totalSentimentBusinesses={totalSentimentBusinesses}
                        seoRank={seoRank}
                        totalSeoBusinesses={totalSeoBusinesses}
                        scoreRank={scoreRank}
                        totalScoreBusinesses={totalScoreBusinesses}
                      />
                    </Box>
                  </ChakraTooltip>

                  <SimpleGrid
                    columns={{ base: 1, md: 2 }}
                    spacing={5}
                    mb={5}
                  >
                    <GetMoreCustomersCard
                      reviews={reviewsForGrowth}
                      traffic={trafficForGrowth}
                      ads={adDataForGrowth}
                    />
                    <KeepMoreCustomersCard
                      businessName={business.name}
                      mapsUri={business.maps_uri}
                      positiveThemes={positiveThemesForRetention}
                      negativeThemes={negativeThemesForRetention}
                    />
                  </SimpleGrid>
                </>
              );
            })()}

            <BusinessReviewOverviewSection
              business={business}
              scoredBusinesses={scoredBusinesses}
              currentScoredIdx={currentScoredIdx}
              scoreRank={scoreRank}
              demoMode={demoMode}
              onSelectCompetitor={handleSelectCompetitor}
              competitorImages={competitorImages}
            />

            <SimpleGrid
              columns={2}
              spacing={5}
            >
              {/* Row 2 */}

              <ChakraTooltip {...evidenceTooltipProps}>
              <Card
                bg='white'
                borderWidth='1px'
                borderColor='gray.100'
                borderRadius='2xl'
                overflow='hidden'
                boxShadow='0 1px 3px rgba(0,0,0,0.04)'
                cursor='pointer'
                onClick={() => navigateToDashboard('/dashboards/gross-growth')}
                _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: 'gray.200' }}
                transition='all 0.2s ease'
              >
                <CardHeader pb={0}>
                  <Heading
                    size='sm'
                    color='gray.800'
                    fontWeight='600'
                    letterSpacing='-0.01em'
                  >
                    Review Activity Trend
                  </Heading>
                </CardHeader>
                <CardBody>
                  <Box h='300px' data-chart="review-activity">
                    <ResponsiveContainer
                      width='100%'
                      height='100%'
                    >
                      <ComposedChart
                        data={monthlyData}
                        margin={{ top: 10, right: 30, left: 0, bottom: 40 }}
                      >
                        <defs>
                          <linearGradient
                            id='colorReviews'
                            x1='0'
                            y1='0'
                            x2='0'
                            y2='1'
                          >
                            <stop
                              offset='0%'
                              stopColor={COLORS.primary}
                              stopOpacity={0.4}
                            />
                            <stop
                              offset='50%'
                              stopColor={COLORS.primary}
                              stopOpacity={0.2}
                            />
                            <stop
                              offset='100%'
                              stopColor={COLORS.primary}
                              stopOpacity={0.05}
                            />
                          </linearGradient>
                          <filter id='reviewsGlow' x='-20%' y='-20%' width='140%' height='140%'>
                            <feGaussianBlur stdDeviation='3' result='coloredBlur'/>
                            <feMerge>
                              <feMergeNode in='coloredBlur'/>
                              <feMergeNode in='SourceGraphic'/>
                            </feMerge>
                          </filter>
                        </defs>
                        <CartesianGrid
                          strokeDasharray='3 3'
                          vertical={false}
                          stroke='#EDF2F7'
                          strokeOpacity={0.8}
                        />
                        <XAxis
                          dataKey='month'
                          axisLine={false}
                          tickLine={false}
                          angle={-45}
                          textAnchor='end'
                          height={60}
                          interval={0}
                          tick={{ fontSize: 14, fontWeight: 600 }}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tickCount={5}
                          tick={{ fontSize: 14, fontWeight: 600 }}
                        />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === 'Competitor Avg') {
                              return [`${value} reviews`, 'Competitor Avg'];
                            }
                            return [`${value} reviews`, 'Volume'];
                          }}
                          labelFormatter={(label) => `${label}`}
                          contentStyle={{
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                            padding: '8px 12px'
                          }}
                        />
                        {hasCompetitorAverage && (
                          <Legend
                            verticalAlign='top'
                            height={28}
                            iconType='plainline'
                            wrapperStyle={{ fontSize: '11px', color: '#718096' }}
                          />
                        )}
                        {hasHistoricalAverage && (
                          <ReferenceLine
                            y={historicalAverageReviews}
                            stroke='#718096'
                            strokeDasharray='2 4'
                            strokeWidth={1.5}
                            strokeOpacity={0.7}
                            ifOverflow='extendDomain'
                            label={{
                              value: `Avg ${historicalAverageDisplay}`,
                              position: 'insideTopRight',
                              fill: '#718096',
                              fontSize: 14,
                              fontWeight: 600
                            }}
                          />
                        )}
                        <Area
                          type='natural'
                          dataKey='count'
                          name='This Business'
                          stroke={COLORS.primary}
                          strokeWidth={2.5}
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          fillOpacity={1}
                          fill='url(#colorReviews)'
                          filter='url(#reviewsGlow)'
                          activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                          isAnimationActive={!isPdfMode}
                          animationDuration={1200}
                          animationEasing='ease-out'
                        />
                        {hasCompetitorAverage && (
                          <Line
                            type='natural'
                            dataKey='competitorAvg'
                            name='Competitor Avg'
                            stroke='#A0AEC0'
                            strokeWidth={1.75}
                            strokeDasharray='5 4'
                            strokeOpacity={0.7}
                            dot={false}
                            activeDot={{
                              r: 4,
                              strokeWidth: 1,
                              stroke: '#fff',
                              fill: '#A0AEC0'
                            }}
                            isAnimationActive={!isPdfMode}
                            animationDuration={1200}
                            animationEasing='ease-out'
                          />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                  <Text
                    fontSize='sm'
                    color={subtitleColor}
                    mt={2}
                    textAlign='center'
                  >
                    {growthComparison >= 0 ? 'Growing' : 'Declining'} review
                    volume with {Math.abs(growthPercentage)}%{' '}
                    {growthComparison >= 0 ? 'increase' : 'decrease'} compared
                    to previous period
                  </Text>
                </CardBody>
              </Card>
              </ChakraTooltip>
              {/* Organic Traffic Trend */}
              <ChakraTooltip {...evidenceTooltipProps}>
              <Card
                bg='white'
                borderWidth='1px'
                borderColor='gray.100'
                borderRadius='2xl'
                overflow='hidden'
                boxShadow='0 1px 3px rgba(0,0,0,0.04)'
                cursor='pointer'
                onClick={() => navigateToDashboard('/dashboards/keyword-intelligence')}
                _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: 'gray.200' }}
                transition='all 0.2s ease'
              >
                <CardHeader pb={0}>
                  <Heading size='sm' color='gray.800' fontWeight='600' letterSpacing='-0.01em'>
                    Organic Traffic Trend
                  </Heading>
                </CardHeader>
                <CardBody>
                  {trafficLoading ? (
                    <Flex justify="center" align="center" h="300px">
                      <Spinner size="lg" color="purple.500" />
                    </Flex>
                  ) : trafficHistory.length > 0 ? (
                    <Box h='300px' data-chart="traffic-trend">
                      <ResponsiveContainer width='100%' height='100%'>
                        <AreaChart
                          data={trafficHistory}
                          margin={{ top: 10, right: 30, left: 0, bottom: 40 }}
                        >
                          <defs>
                            <linearGradient id='colorTraffic' x1='0' y1='0' x2='0' y2='1'>
                              <stop offset='0%' stopColor='#9B51E0' stopOpacity={0.4} />
                              <stop offset='50%' stopColor='#9B51E0' stopOpacity={0.2} />
                              <stop offset='100%' stopColor='#9B51E0' stopOpacity={0.05} />
                            </linearGradient>
                            <filter id='trafficGlow' x='-20%' y='-20%' width='140%' height='140%'>
                              <feGaussianBlur stdDeviation='3' result='coloredBlur'/>
                              <feMerge>
                                <feMergeNode in='coloredBlur'/>
                                <feMergeNode in='SourceGraphic'/>
                              </feMerge>
                            </filter>
                          </defs>
                          <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#EDF2F7' strokeOpacity={0.8} />
                          <XAxis
                            dataKey='month'
                            axisLine={false}
                            tickLine={false}
                            angle={-45}
                            textAnchor='end'
                            height={60}
                          interval={0}
                          tick={{ fontSize: 14, fontWeight: 600 }}
                          />
                          <YAxis axisLine={false} tickLine={false} tickCount={5} tick={{ fontSize: 14, fontWeight: 600 }} />
                          <Tooltip
                            formatter={(value) => [`${value.toLocaleString()} clicks`, 'Organic Traffic']}
                            contentStyle={{
                              backgroundColor: '#fff',
                              borderRadius: '12px',
                              border: '1px solid #E2E8F0',
                              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                              padding: '8px 12px'
                            }}
                          />
                          <Area
                            type='natural'
                            dataKey='organicClicks'
                            stroke='#9B51E0'
                            strokeWidth={2.5}
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            fillOpacity={1}
                            fill='url(#colorTraffic)'
                            filter='url(#trafficGlow)'
                            activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                            isAnimationActive={!isPdfMode}
                            animationDuration={1200}
                            animationEasing='ease-out'
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Flex justify="center" align="center" h="200px">
                      <Text color={subtitleColor}>No traffic data available</Text>
                    </Flex>
                  )}
                </CardBody>
              </Card>
              </ChakraTooltip>
              {/* Sentiment Distribution */}
              <ChakraTooltip {...evidenceTooltipProps}>
              <Card
                bg='white'
                borderWidth='1px'
                borderColor='gray.100'
                borderRadius='2xl'
                overflow='hidden'
                boxShadow='0 1px 3px rgba(0,0,0,0.04)'
                cursor='pointer'
                onClick={() => navigateToDashboard('/dashboards/sentiment')}
                _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: 'gray.200' }}
                transition='all 0.2s ease'
              >
                <CardHeader pb={0}>
                  <Heading
                    size='sm'
                    color='gray.800'
                    fontWeight='600'
                    letterSpacing='-0.01em'
                  >
                    Customer Sentiment
                  </Heading>
                </CardHeader>
                <CardBody>
                  <Flex
                    direction='row'
                    h='300px'
                    data-chart="sentiment-pie"
                  >
                    <Box
                      w='60%'
                      h='100%'
                      ref={boxRef}
                    >
                      <ResponsiveContainer
                        width='100%'
                        height='100%'
                      >
                        <PieChart>
                          <Pie
                            data={sentimentPieData}
                            cx='50%'
                            cy='50%'
                            innerRadius="40%"
                            outerRadius="70%"
                            fill='#8884d8'
                            paddingAngle={5}
                            dataKey='value'
                            label={({ name, percent }) =>
                              `${name} (${(percent * 100).toFixed(1)}%)`
                            }
                          >
                            {sentimentPieData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.fill}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            position={{ x: width / 2 - 90, y: -10 }}
                            formatter={(value) => `${value} reviews`}
                            contentStyle={{
                              backgroundColor: '#fff',
                              borderRadius: '12px',
                              border: '1px solid #E2E8F0',
                              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                              padding: '8px 12px'
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    <VStack
                      align='stretch'
                      spacing={6}
                      p={4}
                      flex='1'
                    >
                      <Text
                        fontWeight='600'
                        fontSize='sm'
                        color='gray.700'
                      >
                        Sentiment Breakdown
                      </Text>
                      <Box>
                        <HStack
                          justify='space-between'
                          mb={1}
                        >
                          <Text mb={0} fontSize='sm'>Positive 😊</Text>
                          <Text mb={0}
                            fontSize='sm'
                            fontWeight='bold'
                            color='green.500'
                          >
                            {positivePercentage}%
                          </Text>
                        </HStack>
                        <Progress
                          value={parseFloat(positivePercentage)}
                          colorScheme='green'
                          size='xs'
                          mb={3}
                          borderRadius='full'
                        />

                        <HStack
                          justify='space-between'
                          mb={1}
                        >
                          <Text mb={0} fontSize='sm'>Neutral 😐</Text>
                          <Text mb={0}
                            fontSize='sm'
                            fontWeight='bold'
                            color='yellow.500'
                          >
                            {neutralPercentage}%
                          </Text>
                        </HStack>
                        <Progress
                          value={parseFloat(neutralPercentage)}
                          colorScheme='yellow'
                          size='xs'
                          mb={3}
                          borderRadius='full'
                        />

                        <HStack
                          justify='space-between'
                          mb={1}
                        >
                          <Text mb={0} fontSize='sm'>Negative 😞</Text>
                          <Text mb={0}
                            fontSize='sm'
                            fontWeight='bold'
                            color='red.500'
                          >
                            {negativePercentage}%
                          </Text>
                        </HStack>
                        <Progress
                          value={parseFloat(negativePercentage)}
                          colorScheme='red'
                          size='xs'
                          borderRadius='full'
                        />
                      </Box>
                    </VStack>
                  </Flex>
                </CardBody>
              </Card>
              </ChakraTooltip>
              {/* Cumulative Reviews Over Time - positioned next to Customer Sentiment */}
              <ChakraTooltip {...evidenceTooltipProps}>
              <Card
                bg='white'
                borderWidth='1px'
                borderColor='gray.100'
                borderRadius='2xl'
                overflow='hidden'
                boxShadow='0 1px 3px rgba(0,0,0,0.04)'
                cursor='pointer'
                onClick={() => navigateToDashboard('/dashboards/total-reviews')}
                _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: 'gray.200' }}
                transition='all 0.2s ease'
              >
                <CardHeader pb={0}>
                  <Heading
                    size='sm'
                    color='gray.800'
                    fontWeight='600'
                    letterSpacing='-0.01em'
                  >
                    Total Reviews Over Time
                  </Heading>
                </CardHeader>
                <CardBody>
                  {cumulativeReviewsData.length > 0 ? (
                    <Box h='300px' data-chart="cumulative-reviews">
                      <ResponsiveContainer width='100%' height='100%'>
                        <LineChart
                          data={cumulativeReviewsData}
                          margin={{ top: 10, right: 30, left: 10, bottom: 40 }}
                        >
                          <CartesianGrid
                            strokeDasharray='3 3'
                            stroke='#EDF2F7'
                            vertical={false}
                          />
                          <XAxis
                            dataKey='month'
                            axisLine={false}
                            tickLine={false}
                            angle={-45}
                            textAnchor='end'
                            height={60}
                          interval={0}
                          tick={{ fontSize: 14, fontWeight: 600 }}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tickCount={5}
                            tick={{ fontSize: 14, fontWeight: 600 }}
                            width={40}
                            tickFormatter={(value) => value.toLocaleString()}
                          />
                          <Tooltip
                            formatter={(value) => [`${value.toLocaleString()} total reviews`, 'Cumulative']}
                            labelFormatter={(label) => label}
                            contentStyle={{
                              backgroundColor: '#fff',
                              borderRadius: '12px',
                              border: '1px solid #E2E8F0',
                              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                              padding: '8px 12px'
                            }}
                          />
                          <Legend
                            verticalAlign='top'
                            height={36}
                            formatter={() => 'Cumulative Reviews'}
                          />
                          <Line
                            type='monotone'
                            dataKey='cumulativeReviews'
                            name='Cumulative Reviews'
                            stroke={COLORS.success}
                            strokeWidth={2}
                            dot={{ fill: COLORS.success, strokeWidth: 2, r: 3 }}
                            activeDot={{ r: 6 }}
                            isAnimationActive={!isPdfMode}
                            animationDuration={1000}
                            animationBegin={0}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Flex justify="center" align="center" h="300px">
                      <Text color={subtitleColor}>No review history data available</Text>
                    </Flex>
                  )}
                  <Text
                    fontSize='sm'
                    color={subtitleColor}
                    mt={2}
                    textAlign='center'
                  >
                    Cumulative review count showing business growth over time
                  </Text>
                </CardBody>
              </Card>
              </ChakraTooltip>
              {/* Row 3 */}
              {/* Monthly Ratings Breakdown */}
              <ChakraTooltip {...evidenceTooltipProps}>
              <Card
                bg='white'
                borderWidth='1px'
                borderColor='gray.100'
                borderRadius='2xl'
                overflow='hidden'
                boxShadow='0 1px 3px rgba(0,0,0,0.04)'
                mb={4}
                gridColumn='span 2'
                cursor='pointer'
                onClick={() => navigateToDashboard('/dashboards/sentiment')}
                _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: 'gray.200' }}
                transition='all 0.2s ease'
              >
                <CardHeader pb={0}>
                  <Heading
                    size='sm'
                    color='gray.800'
                    fontWeight='600'
                    letterSpacing='-0.01em'
                  >
                    Monthly Sentiment Breakdown
                  </Heading>
                </CardHeader>
                <CardBody>
                  <Box h='300px' data-chart="monthly-sentiment">
                    <ResponsiveContainer
                      width='100%'
                      height='100%'
                    >
                      <BarChart
                        data={monthlyData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                      >
                        <CartesianGrid
                          strokeDasharray='3 3'
                          vertical={false}
                          stroke='#EDF2F7'
                        />
                        <XAxis
                          dataKey='month'
                          angle={-45}
                          textAnchor='end'
                          height={60}
                          interval={0}
                          tick={{ fontSize: 14, fontWeight: 600 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tickCount={5}
                          tick={{ fontSize: 14, fontWeight: 600 }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                            padding: '8px 12px'
                          }}
                          formatter={(value, name) => [
                            `${value} reviews`,
                            name.charAt(0).toUpperCase() + name.slice(1)
                          ]}
                        />
                        <Legend
                          verticalAlign='top'
                          height={36}
                          wrapperStyle={{
                            paddingTop: '10px'
                          }}
                        />
                        <Bar
                          dataKey='positive'
                          stackId='a'
                          fill='#48BB78'
                          name='Positive'
                          isAnimationActive={!isPdfMode}
                          animationDuration={1000}
                          animationBegin={0}
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar
                          dataKey='neutral'
                          stackId='a'
                          fill='#ECC94B'
                          name='Neutral'
                          isAnimationActive={!isPdfMode}
                          animationDuration={1000}
                          animationBegin={0}
                        />
                        <Bar
                          dataKey='negative'
                          stackId='a'
                          fill='#E53E3E'
                          name='Negative'
                          isAnimationActive={!isPdfMode}
                          animationDuration={1000}
                          animationBegin={0}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardBody>
              </Card>
              </ChakraTooltip>
            </SimpleGrid>
            <ChakraTooltip {...evidenceTooltipProps}>
            <Card
              bg='white'
              borderWidth='1px'
              borderColor='gray.100'
              borderRadius='2xl'
              overflow='hidden'
              boxShadow='0 1px 3px rgba(0,0,0,0.04)'
              mb={5}
              px={5}
              pb={5}
              cursor={demoMode ? 'default' : 'pointer'}
              onClick={openSwotEvidence}
              _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: 'gray.200' }}
              transition='all 0.2s ease'
            >
              <CardHeader
                pb={0}
                px={0}
              >
                <Heading
                  size='sm'
                  color='gray.800'
                  fontWeight='600'
                  letterSpacing='-0.01em'
                >
                  SWOT Analysis
                </Heading>
              </CardHeader>
              <Text color='gray.500' fontSize='sm' lineHeight='1.6'>
                Internal SWOT analysis based on 5-star and 1-star review insights.
                Strengths and opportunities reflect what happy customers love,
                while weaknesses and threats highlight operational areas to improve.
              </Text>

              {swotInsights ? (
                <SwotAnalysis content={swotInsights} />
              ) : swotLoading ? (
                <Flex align='center' justify='center' py={10}>
                  <Spinner size='lg' color='indigo.500' mr={3} />
                  <Text color='gray.600' fontSize='sm'>
                    Generating SWOT analysis from this business&apos;s 5-star and
                    1-star review insights&hellip;
                  </Text>
                </Flex>
              ) : (
                <Flex align='center' justify='center' py={6}>
                  <Text color='red.500' fontSize='sm'>
                    {swotError ||
                      'SWOT analysis is unavailable for this business right now.'}
                  </Text>
                </Flex>
              )}
            </Card>
            </ChakraTooltip>
            {/* Pricing Perception Section */}
            <ChakraTooltip {...evidenceTooltipProps}>
            <Card
              bg='white'
              borderWidth='1px'
              borderColor='gray.100'
              borderRadius='2xl'
              overflow='hidden'
              boxShadow='0 1px 3px rgba(0,0,0,0.04)'
              mb={5}
              px={5}
              pb={5}
              cursor='pointer'
              onClick={() => navigateToDashboard('/dashboards/pricing')}
              _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: 'gray.200' }}
              transition='all 0.2s ease'
            >
              <CardHeader pb={2} px={0}>
                <HStack spacing={3} align='center'>
                  <Flex
                    w='32px'
                    h='32px'
                    bg='green.50'
                    borderRadius='xl'
                    justify='center'
                    align='center'
                  >
                    <Icon as={FaDollarSign} color='green.400' boxSize={4} />
                  </Flex>
                  <Heading mb={0} size='sm' color='gray.800' fontWeight='600' letterSpacing='-0.01em'>
                    Pricing Perception
                  </Heading>
                </HStack>
              </CardHeader>

              {pricingLoading ? (
                <Flex justify='center' py={8}>
                  <Spinner size='lg' color='green.500' />
                </Flex>
              ) : pricingData ? (
                <VStack align='stretch' spacing={4} mt={2}>
                  {/* Price Tier Badge + Market Rank */}
                  <Flex
                    justify='space-between'
                    align='center'
                    bg='gray.50'
                    p={4}
                    borderRadius='xl'
                    wrap='wrap'
                    gap={3}
                    border='1px solid'
                    borderColor='gray.100'
                  >
                    <HStack spacing={4}>
                      <Badge
                        colorScheme={getPriceLevelColor(pricingData.price_level || pricingData.tier_numeric)}
                        fontSize='2xl'
                        px={4}
                        py={2}
                        borderRadius='lg'
                        fontWeight='800'
                      >
                        {pricingData.price_label || formatPriceLevel(pricingData.price_level || pricingData.tier_numeric)}
                      </Badge>
                      <VStack align='start' spacing={0}>
                        <Text fontWeight='600' color='gray.700' fontSize='sm'>
                          {pricingData.perception_label || pricingData.classification || (
                            (pricingData.price_level || pricingData.tier_numeric) <= 2 ? 'Value Advantage' : 'Premium Perception'
                          )}
                        </Text>
                        {(pricingData.perception_score != null || pricingData.avg_score != null) && (
                          <Text fontSize='xs' color='gray.500'>
                            Score: {(pricingData.perception_score ?? pricingData.avg_score ?? 0).toFixed?.(1) ?? pricingData.perception_score ?? pricingData.avg_score}
                          </Text>
                        )}
                      </VStack>
                    </HStack>
                    {(pricingData.market_rank || pricingData.market_ranking) && (
                      <Text fontSize='sm' color='gray.600' fontWeight='500'>
                        Ranked #{pricingData.market_rank?.rank || pricingData.market_ranking?.rank || pricingData.market_rank} most affordable out of{' '}
                        {pricingData.market_rank?.total || pricingData.market_ranking?.total || pricingData.market_total} businesses
                      </Text>
                    )}
                  </Flex>

                  {/* Keyword Summary */}
                  {(pricingData.top_positive_keywords?.length > 0 || pricingData.top_negative_keywords?.length > 0 ||
                    pricingData.pricing_reasoning?.cheap_keyword_counts || pricingData.pricing_reasoning?.expensive_keyword_counts) && (
                    <SimpleGrid columns={2} spacing={3}>
                      {/* Value Keywords */}
                      <Box bg='green.50' p={3} borderRadius='xl' border='1px solid' borderColor='green.100'>
                        <Text fontWeight='600' color='green.700' fontSize='sm' mb={2}>
                          Value Keywords
                        </Text>
                        <Wrap spacing={1}>
                          {(pricingData.top_positive_keywords ||
                            Object.entries(pricingData.pricing_reasoning?.cheap_keyword_counts || {})
                              .sort((a, b) => b[1] - a[1])
                              .slice(0, 6)
                              .map(([kw, cnt]) => ({ keyword: kw, count: cnt }))
                          )?.map((item, idx) => (
                            <WrapItem key={idx}>
                              <Tag size='sm' colorScheme='green' variant='subtle'>
                                <TagLabel>
                                  {item.keyword || item} {item.count ? `(${item.count}x)` : ''}
                                </TagLabel>
                              </Tag>
                            </WrapItem>
                          ))}
                          {!(pricingData.top_positive_keywords?.length > 0 ||
                            Object.keys(pricingData.pricing_reasoning?.cheap_keyword_counts || {}).length > 0) && (
                            <Text fontSize='xs' color='gray.500'>No value keywords found</Text>
                          )}
                        </Wrap>
                      </Box>
                      {/* Expensive Keywords */}
                      <Box bg='red.50' p={3} borderRadius='xl' border='1px solid' borderColor='red.100'>
                        <Text fontWeight='600' color='red.700' fontSize='sm' mb={2}>
                          Expensive Keywords
                        </Text>
                        <Wrap spacing={1}>
                          {(pricingData.top_negative_keywords ||
                            Object.entries(pricingData.pricing_reasoning?.expensive_keyword_counts || {})
                              .sort((a, b) => b[1] - a[1])
                              .slice(0, 6)
                              .map(([kw, cnt]) => ({ keyword: kw, count: cnt }))
                          )?.map((item, idx) => (
                            <WrapItem key={idx}>
                              <Tag size='sm' colorScheme='red' variant='subtle'>
                                <TagLabel>
                                  {item.keyword || item} {item.count ? `(${item.count}x)` : ''}
                                </TagLabel>
                              </Tag>
                            </WrapItem>
                          ))}
                          {!(pricingData.top_negative_keywords?.length > 0 ||
                            Object.keys(pricingData.pricing_reasoning?.expensive_keyword_counts || {}).length > 0) && (
                            <Text fontSize='xs' color='gray.500'>No expensive keywords found</Text>
                          )}
                        </Wrap>
                      </Box>
                    </SimpleGrid>
                  )}

                  {/* Review Evidence Cards */}
                  {(pricingData.mentions?.length > 0 || pricingData.price_reviews?.length > 0) && (
                    <Box>
                      <Text fontWeight='600' color='gray.700' fontSize='sm' mb={3}>
                        Review Evidence
                      </Text>
                      <VStack align='stretch' spacing={2}>
                        {(pricingData.mentions || pricingData.price_reviews || []).slice(0, 5).map((mention, idx) => {
                          const text = mention.context || mention.review_snippet || mention.text || '';
                          const keyword = mention.keyword || (mention.cheap_keywords?.[0]) || (mention.expensive_keywords?.[0]) || '';
                          const stars = mention.stars || mention.rating || 0;
                          const reviewer = mention.reviewer_name || mention.reviewer || 'Anonymous';
                          const sentiment = mention.effective_sentiment || mention.effective_bucket || mention.sentiment;
                          const isValue = sentiment === 'cheap' || sentiment === 'positive' || sentiment === 'STRONG_POSITIVE' || sentiment === 'MILD_POSITIVE' || mention.cheap_keywords?.length > 0;

                          // Bold all matched keywords in the text (keyword may be comma-separated)
                          let displayText = text;
                          if (keyword && text) {
                            const keywords = keyword.split(',').map(k => k.trim()).filter(Boolean);
                            const escapedKeywords = keywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
                            const keywordRegex = new RegExp(`(${escapedKeywords.join('|')})`, 'gi');
                            const parts = text.split(keywordRegex);
                            displayText = parts.map((part, i) =>
                              keywordRegex.test(part)
                                ? <Text as='span' key={i} fontWeight='700' bg={isValue ? 'green.100' : 'red.100'} px={0.5} borderRadius='sm'>{part}</Text>
                                : part
                            );
                          }

                          return (
                            <Box
                              key={idx}
                              p={3}
                              bg='white'
                              border='1px solid'
                              borderColor={isValue ? 'green.100' : 'red.100'}
                              borderRadius='xl'
                              borderLeftWidth='3px'
                              borderLeftColor={isValue ? 'green.400' : 'red.400'}
                            >
                              <HStack justify='space-between' mb={1}>
                                <HStack spacing={2}>
                                  <Icon as={FaQuoteLeft} boxSize={3} color='gray.300' />
                                  {mention.review_url ? (
                                    <Link
                                      href={mention.review_url}
                                      isExternal
                                      fontSize='xs'
                                      fontWeight='500'
                                      color='blue.500'
                                      _hover={{ textDecoration: 'underline' }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {reviewer} <ExternalLinkIcon mx='2px' boxSize={2.5} />
                                    </Link>
                                  ) : (
                                    <Text mb={0} fontSize='xs' fontWeight='500' color='gray.500'>
                                      {reviewer}
                                    </Text>
                                  )}
                                </HStack>
                                <HStack spacing={1}>
                                  {[...Array(5)].map((_, i) => (
                                    <Icon
                                      key={i}
                                      as={StarIcon}
                                      boxSize={3}
                                      color={i < stars ? 'yellow.400' : 'gray.200'}
                                    />
                                  ))}
                                </HStack>
                              </HStack>
                              <Text fontSize='sm' color='gray.700' lineHeight='1.6' noOfLines={3}>
                                {Array.isArray(displayText) ? displayText : `"${displayText}"`}
                              </Text>
                            </Box>
                          );
                        })}
                      </VStack>
                    </Box>
                  )}
                </VStack>
              ) : (
                <Text color='gray.500' fontSize='sm' py={4}>
                  No pricing perception data available. Run pricing analysis to generate insights.
                </Text>
              )}
            </Card>
            </ChakraTooltip>

            {/* Ads Running Section */}
            <ChakraTooltip {...evidenceTooltipProps}>
            <Card
              bg='white'
              borderWidth='1px'
              borderColor='gray.100'
              borderRadius='2xl'
              overflow='hidden'
              boxShadow='0 1px 3px rgba(0,0,0,0.04)'
              mb={5}
              px={5}
              pb={5}
              cursor='pointer'
              onClick={() => navigateToDashboard('/dashboards/ads-intelligence')}
              _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: 'gray.200' }}
              transition='all 0.2s ease'
            >
              <CardHeader pb={2} px={0}>
                <HStack spacing={3} align='center'>
                  <Flex
                    w='32px'
                    h='32px'
                    bg='purple.50'
                    borderRadius='xl'
                    justify='center'
                    align='center'
                  >
                    <Icon as={FaBullhorn} color='purple.400' boxSize={4} />
                  </Flex>
                  <Heading mb={0} size='sm' color='gray.800' fontWeight='600' letterSpacing='-0.01em'>
                    Ads Running
                  </Heading>
                </HStack>
              </CardHeader>

              {(() => {
                const ads = getAdHistory(business);
                const marketBusinesses =
                  sortedByTotal && sortedByTotal.length > 0
                    ? sortedByTotal
                    : sortedByGrowth && sortedByGrowth.length > 0
                      ? sortedByGrowth
                      : sortedByTraffic && sortedByTraffic.length > 0
                        ? sortedByTraffic
                        : sortedBySentiment && sortedBySentiment.length > 0
                          ? sortedBySentiment
                          : [];
                const adRanking = getBusinessAdRank(business, marketBusinesses);
                const formatAvg = (value) => {
                  if (!value || value === 0) return '0';
                  return value >= 10 ? value.toFixed(0) : value.toFixed(1);
                };
                const adRankColor = (rank, total) => {
                  if (!rank || !total) return 'gray';
                  const pct = rank / total;
                  if (rank === 1) return 'yellow';
                  if (pct <= 0.25) return 'green';
                  if (pct <= 0.5) return 'blue';
                  return 'gray';
                };

                if (!ads || ads.length === 0) {
                  return (
                    <VStack align='stretch' spacing={3} mt={2}>
                      {adRanking.totalBusinesses > 0 && (
                        <SimpleGrid columns={4} spacing={1.5}>
                          <StatPill
                            icon={FaBullhorn}
                            color='gray'
                            label='Ads Running'
                            value='0'
                            sublabel='this business'
                          />
                          <StatPill
                            icon={FaTrophy}
                            color='gray'
                            label='Market Rank'
                            value={adRanking.rank ? `#${adRanking.rank}` : 'N/A'}
                            sublabel={`of ${adRanking.totalBusinesses}`}
                          />
                          <StatPill
                            icon={FaChartBar}
                            color='gray'
                            label='Market Avg'
                            value={formatAvg(adRanking.avgAdsAcrossMarket)}
                            sublabel='across market'
                          />
                          <StatPill
                            icon={FaUsers}
                            color='gray'
                            label='Advertiser Avg'
                            value={formatAvg(adRanking.avgAdsAcrossAdvertisers)}
                            sublabel={`${adRanking.totalAdvertisers} advertisers`}
                          />
                        </SimpleGrid>
                      )}
                      <Text color='gray.500' fontSize='sm' py={2}>
                        No ads found for {business.name}.
                      </Text>
                    </VStack>
                  );
                }

                const rankColor = adRankColor(
                  adRanking.rank,
                  adRanking.totalBusinesses,
                );
                const advertiserRankColor = adRankColor(
                  adRanking.advertiserRank,
                  adRanking.totalAdvertisers,
                );
                return (
                  <VStack align='stretch' spacing={3} mt={2}>
                    {/* Ad ranking summary */}
                    {adRanking.totalBusinesses > 0 && (
                      <SimpleGrid columns={4} spacing={1.5}>
                        <StatPill
                          icon={FaBullhorn}
                          color='purple'
                          label='Ads Running'
                          value={ads.length >= 100 ? '100+' : ads.length}
                          sublabel='this business'
                        />
                        <StatPill
                          icon={FaTrophy}
                          color={rankColor}
                          label='Market Rank'
                          value={adRanking.rank ? `#${adRanking.rank}` : 'N/A'}
                          sublabel={`of ${adRanking.totalBusinesses}`}
                        />
                        <StatPill
                          icon={FaUsers}
                          color={advertiserRankColor}
                          label='Advertisers'
                          value={adRanking.advertiserRank ? `#${adRanking.advertiserRank}` : 'N/A'}
                          sublabel={`of ${adRanking.totalAdvertisers}`}
                        />
                        <StatPill
                          icon={FaChartBar}
                          color='blue'
                          label='Market Avg'
                          value={formatAvg(adRanking.avgAdsAcrossMarket)}
                          sublabel={`advertiser avg ${formatAvg(adRanking.avgAdsAcrossAdvertisers)}`}
                        />
                      </SimpleGrid>
                    )}

                    <Text fontSize='xs' color='gray.500'>
                      Showing {Math.min(ads.length, 6)} of {ads.length >= 100 ? '100+' : ads.length} ad{ads.length !== 1 ? 's' : ''}
                    </Text>
                    <SimpleGrid columns={2} spacing={3}>
                      {ads.slice(0, 6).map((ad, idx) => (
                        <Box
                          key={idx}
                          bg='white'
                          borderWidth='1px'
                          borderColor='gray.100'
                          borderRadius='xl'
                          p={3}
                          shadow='0 1px 3px rgba(0,0,0,0.04)'
                          _hover={{ shadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: 'gray.200' }}
                          transition='all 0.2s'
                        >
                          <HStack spacing={2} mb={2}>
                            <Badge bg='yellow.100' color='gray.700' fontSize='2xs' px={1.5} py={0.5} borderRadius='sm'>
                              Sponsored
                            </Badge>
                            {ad.position && (
                              <Badge colorScheme='green' fontSize='2xs'>
                                Pos #{ad.position}
                              </Badge>
                            )}
                          </HStack>

                          <HStack spacing={1} mb={1}>
                            <Icon as={FaGoogle} color='gray.400' boxSize={2.5} />
                            <Text mb={0} fontSize='xs' color='green.700' noOfLines={1}>
                              {ad.url ? ad.url.replace(/^https?:\/\//, '').split('/')[0] : business.spyfu_data?.domain || 'N/A'}
                            </Text>
                          </HStack>

                          <Text
                            fontSize='sm'
                            fontWeight='semibold'
                            color='blue.700'
                            mb={1}
                            noOfLines={2}
                          >
                            {ad.title || 'Untitled Ad'}
                          </Text>

                          <Text fontSize='xs' color='gray.600' noOfLines={2} mb={2}>
                            {ad.body || 'No description available'}
                          </Text>

                          {ad.searchDateId && (
                            <Text fontSize='2xs' color='gray.400'>
                              Seen: {formatSearchDate(ad.searchDateId)}
                            </Text>
                          )}

                          {ad.keywords && ad.keywords.length > 0 && (
                            <Wrap spacing={1} mt={1}>
                              {ad.keywords.slice(0, 3).map((kw, kwIdx) => (
                                <WrapItem key={kwIdx}>
                                  <Tag size='sm' colorScheme='blue' variant='subtle'>
                                    <TagLabel fontSize='2xs'>{kw}</TagLabel>
                                  </Tag>
                                </WrapItem>
                              ))}
                            </Wrap>
                          )}
                        </Box>
                      ))}
                    </SimpleGrid>
                  </VStack>
                );
              })()}
            </Card>
            </ChakraTooltip>

            {/* Business Overview Section */}
            <ChakraTooltip {...evidenceTooltipProps}>
            <Card
              bg='white'
              borderWidth='1px'
              borderColor='gray.100'
              borderRadius='2xl'
              overflow='hidden'
              boxShadow='0 1px 3px rgba(0,0,0,0.04)'
              mb={5}
              px={5}
              pb={5}
              cursor='pointer'
              onClick={() => navigateToDashboard('/dashboards/business-insights')}
              _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: 'gray.200' }}
              transition='all 0.2s ease'
            >
              <CardHeader pb={2} px={0}>
                <Heading size='md' color='gray.800' textAlign='center' fontWeight='600' letterSpacing='-0.02em'>
                  How {business.name} Stacks Up
                </Heading>
              </CardHeader>
              <VStack align='stretch' spacing={3} mt={2}>
                {(() => {
                  const metrics = [
                    { rank: growthRank, total: totalGrowthBusinesses, label: 'Growth', icon: '📈' },
                    { rank: totalReviewsRank, total: totalTotalReviewsBusinesses, label: 'Reviews', icon: '📊' },
                    { rank: sentimentRank, total: totalSentimentBusinesses, label: 'Sentiment', icon: '💬' },
                    { rank: trafficRank, total: totalTrafficBusinesses, label: 'Traffic', icon: '🌐' },
                  ].filter(m => m.rank !== 'N/A' && m.total !== 'N/A' && m.total > 0);

                  if (metrics.length === 0) return (
                    <Text color='gray.500' fontSize='sm' textAlign='center' py={4}>
                      Not enough data to compare against this market yet.
                    </Text>
                  );

                  const getMetricStatus = (rank, total) => {
                    const pct = rank / total;
                    if (rank === 1) return { color: 'green', label: 'Market Leader' };
                    if (pct <= 0.15) return { color: 'green', label: 'Top Performer' };
                    if (pct <= 0.25) return { color: 'green', label: 'Beats Most' };
                    if (pct <= 0.5) return { color: 'blue', label: 'Above Average' };
                    if (pct <= 0.75) return { color: 'orange', label: 'Below Average' };
                    return { color: 'red', label: 'Needs Work' };
                  };

                  const leading = metrics.filter(m => m.rank / m.total <= 0.25).map(m => m.label.toLowerCase());
                  const trailing = metrics.filter(m => m.rank / m.total > 0.6).map(m => m.label.toLowerCase());
                  const avgPct = metrics.reduce((sum, m) => sum + m.rank / m.total, 0) / metrics.length;

                  return (
                    <>
                      <SimpleGrid columns={4} spacing={2}>
                        {metrics.map((m) => {
                          const status = getMetricStatus(m.rank, m.total);
                          return (
                            <Box
                              key={m.label}
                              bg={`${status.color}.50`}
                              p={3}
                              borderRadius='lg'
                              textAlign='center'
                            >
                              <Text fontSize='lg' mb={1}>{m.icon}</Text>
                              <Text fontSize='xs' fontWeight='700' color={`${status.color}.700`} textTransform='uppercase' letterSpacing='0.05em'>
                                {m.label}
                              </Text>
                              <Text fontSize='sm' fontWeight='700' color={`${status.color}.600`}>
                                {status.label}
                              </Text>
                              <Text fontSize='2xs' color={`${status.color}.500`} mt={0.5}>
                                #{m.rank} of {m.total}
                              </Text>
                            </Box>
                          );
                        })}
                      </SimpleGrid>

                      <Box
                        bg={avgPct <= 0.3 ? 'green.50' : avgPct <= 0.55 ? 'blue.50' : avgPct <= 0.75 ? 'orange.50' : 'red.50'}
                        p={4}
                        borderRadius='lg'
                        borderLeft='3px solid'
                        borderLeftColor={avgPct <= 0.3 ? 'green.400' : avgPct <= 0.55 ? 'blue.400' : avgPct <= 0.75 ? 'orange.400' : 'red.400'}
                      >
                        <Text
                          fontWeight='700'
                          color={avgPct <= 0.3 ? 'green.700' : avgPct <= 0.55 ? 'blue.700' : avgPct <= 0.75 ? 'orange.700' : 'red.700'}
                          fontSize='sm'
                        >
                          {business.name}{' '}
                          {(() => {
                            if (avgPct <= 0.3) {
                              const strengthDesc = leading.length === 1
                                ? `especially in ${leading[0]}`
                                : leading.length > 1
                                  ? `especially in ${leading.join(' & ')}`
                                  : 'across the board';
                              return `is a top performer in this market, ${strengthDesc}.`;
                            } else if (avgPct <= 0.55) {
                              const weakDesc = trailing.length
                                ? ` There's room to improve in ${trailing.join(' & ')}.`
                                : '';
                              return `is holding its own against competitors.${weakDesc}`;
                            } else if (avgPct <= 0.75) {
                              const weakDesc = trailing.length
                                ? ` Competitors are ahead in ${trailing.join(' & ')}.`
                                : '';
                              return `has some catching up to do.${weakDesc}`;
                            } else {
                              const weakDesc = trailing.length
                                ? ` The biggest opportunities are in ${trailing.join(' & ')}.`
                                : '';
                              return `is behind most competitors and needs attention.${weakDesc}`;
                            }
                          })()}
                        </Text>
                        <Text
                          color={avgPct <= 0.3 ? 'green.600' : avgPct <= 0.55 ? 'blue.600' : avgPct <= 0.75 ? 'orange.600' : 'red.600'}
                          fontSize='xs'
                          mt={1}
                        >
                          {business.rating_avg?.toFixed(1) || 'N/A'}★ across {(business.rating_count || 0).toLocaleString()} reviews
                          {!business.website_uri ? ' · No website' : ''}
                          {trafficHistory.length > 0 && trafficHistory[trafficHistory.length - 1]?.organicClicks > 0
                            ? ` · ${trafficHistory[trafficHistory.length - 1].organicClicks.toLocaleString()} monthly clicks`
                            : ''}
                          {monthlyGrowthRate > 0 ? ` · ${monthlyGrowthRate} new reviews last month` : ''}
                        </Text>
                      </Box>
                    </>
                  );
                })()}
              </VStack>
            </Card>
            </ChakraTooltip>
          </Box>
        </Box>
      </ContentWrap>
      <SwotEvidenceModal
        isOpen={swotEvidenceOpen}
        onClose={closeSwotEvidence}
        loading={swotEvidenceLoading}
        error={swotEvidenceError}
        evidence={swotEvidence}
        businessName={business?.name}
      />
    </ContainerWrap>
  );
};

// --- SWOT Evidence Sub-Modal -------------------------------------------------
// Surfaces the actual high- and low-rated review excerpts that fed the AI
// SWOT analysis. If the business has no excerpts on file we say so plainly
// instead of pretending the AI summary was grounded in source material we
// can show.
const SwotEvidenceReviewCard = ({ review, tone }) => {
  const accent = tone === 'low' ? 'red' : 'green';
  const stars = Math.max(0, Math.min(5, Number(review?.rating) || 0));
  return (
    <Box
      bg='white'
      borderWidth='1px'
      borderColor={`${accent}.100`}
      borderLeftWidth='3px'
      borderLeftColor={`${accent}.400`}
      borderRadius='xl'
      p={3}
    >
      <HStack justify='space-between' mb={1} align='center'>
        <HStack spacing={2} align='center'>
          <Icon as={FaQuoteLeft} boxSize={3} color='gray.300' />
          {review?.review_url ? (
            <Link
              href={review.review_url}
              isExternal
              fontSize='xs'
              fontWeight='600'
              color='blue.500'
              _hover={{ textDecoration: 'underline' }}
              onClick={(e) => e.stopPropagation()}
            >
              {review.reviewer_name || 'Anonymous reviewer'}{' '}
              <ExternalLinkIcon mx='2px' boxSize={2.5} />
            </Link>
          ) : (
            <Text fontSize='xs' fontWeight='600' color='gray.600'>
              {review?.reviewer_name || 'Anonymous reviewer'}
            </Text>
          )}
        </HStack>
        <HStack spacing={1}>
          {[...Array(5)].map((_, i) => (
            <Icon
              key={i}
              as={StarIcon}
              boxSize={3}
              color={i < stars ? 'yellow.400' : 'gray.200'}
            />
          ))}
        </HStack>
      </HStack>
      <Text fontSize='sm' color='gray.700' lineHeight='1.6'>
        &ldquo;{review?.text}&rdquo;
      </Text>
    </Box>
  );
};

const SwotEvidenceModal = ({
  isOpen,
  onClose,
  loading,
  error,
  evidence,
  businessName,
}) => {
  const high = evidence?.high_reviews || [];
  const low = evidence?.low_reviews || [];
  const hasAny = high.length > 0 || low.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='3xl' isCentered scrollBehavior='inside'>
      <ModalOverlay bg='blackAlpha.500' backdropFilter='blur(6px)' />
      <ModalContent borderRadius='2xl' overflow='hidden'>
        <ModalHeader
          borderBottom='1px solid'
          borderColor='gray.100'
          px={6}
          py={4}
        >
          <VStack align='start' spacing={0.5}>
            <Text fontSize='xs' fontWeight='700' color='gray.500' textTransform='uppercase' letterSpacing='0.08em'>
              Click for Evidence
            </Text>
            <Heading size='md' color='gray.800' fontWeight='700' letterSpacing='-0.01em'>
              SWOT source reviews{businessName ? ` · ${businessName}` : ''}
            </Heading>
            <Text fontSize='xs' color='gray.500'>
              These are the actual customer reviews the AI used to build the
              SWOT analysis.
            </Text>
          </VStack>
          <ModalCloseButton top={4} right={4} borderRadius='full' />
        </ModalHeader>
        <Box px={6} py={5} bg='gray.50'>
          {loading ? (
            <Flex justify='center' align='center' py={10}>
              <Spinner size='lg' color='blue.500' mr={3} />
              <Text color='gray.600' fontSize='sm'>Loading source reviews&hellip;</Text>
            </Flex>
          ) : error ? (
            <Flex
              direction='column'
              align='center'
              justify='center'
              py={10}
              px={4}
              bg='white'
              borderRadius='xl'
              border='1px solid'
              borderColor='red.100'
            >
              <Text color='red.500' fontSize='sm' fontWeight='600' mb={1}>
                {error}
              </Text>
              <Text color='gray.500' fontSize='xs'>
                Please try again in a moment.
              </Text>
            </Flex>
          ) : !hasAny ? (
            <Flex
              direction='column'
              align='center'
              justify='center'
              py={10}
              px={4}
              bg='white'
              borderRadius='xl'
              border='1px dashed'
              borderColor='gray.200'
              textAlign='center'
            >
              <Icon as={FaQuoteLeft} boxSize={5} color='gray.300' mb={3} />
              <Text fontSize='sm' fontWeight='600' color='gray.700' mb={1}>
                No source reviews to reference
              </Text>
              <Text fontSize='xs' color='gray.500' maxW='420px'>
                We don&apos;t have any high- or low-rated review excerpts stored
                for {businessName || 'this business'} yet, so the AI SWOT
                summary above can&apos;t be backed by individual quotes.
              </Text>
            </Flex>
          ) : (
            <SimpleGrid columns={2} spacing={4}>
              <Box>
                <HStack spacing={2} mb={3}>
                  <Flex w='28px' h='28px' bg='green.50' borderRadius='md' justify='center' align='center'>
                    <Icon as={FaThumbsUp} color='green.500' boxSize={3.5} />
                  </Flex>
                  <Text fontSize='sm' fontWeight='700' color='green.700' textTransform='uppercase' letterSpacing='0.05em'>
                    High-rated reviews ({high.length})
                  </Text>
                </HStack>
                {high.length === 0 ? (
                  <Box
                    bg='white'
                    border='1px dashed'
                    borderColor='gray.200'
                    borderRadius='xl'
                    p={4}
                  >
                    <Text fontSize='xs' color='gray.500'>
                      No 4-5 star excerpts stored for this business.
                    </Text>
                  </Box>
                ) : (
                  <VStack align='stretch' spacing={2}>
                    {high.map((r, idx) => (
                      <SwotEvidenceReviewCard key={`hi-${idx}`} review={r} tone='high' />
                    ))}
                  </VStack>
                )}
              </Box>
              <Box>
                <HStack spacing={2} mb={3}>
                  <Flex w='28px' h='28px' bg='red.50' borderRadius='md' justify='center' align='center'>
                    <Icon as={FaThumbsDown} color='red.500' boxSize={3.5} />
                  </Flex>
                  <Text fontSize='sm' fontWeight='700' color='red.700' textTransform='uppercase' letterSpacing='0.05em'>
                    Low-rated reviews ({low.length})
                  </Text>
                </HStack>
                {low.length === 0 ? (
                  <Box
                    bg='white'
                    border='1px dashed'
                    borderColor='gray.200'
                    borderRadius='xl'
                    p={4}
                  >
                    <Text fontSize='xs' color='gray.500'>
                      No 1-2 star excerpts stored for this business.
                    </Text>
                  </Box>
                ) : (
                  <VStack align='stretch' spacing={2}>
                    {low.map((r, idx) => (
                      <SwotEvidenceReviewCard key={`lo-${idx}`} review={r} tone='low' />
                    ))}
                  </VStack>
                )}
              </Box>
            </SimpleGrid>
          )}
        </Box>
      </ModalContent>
    </Modal>
  );
};

export default BusinessReviewModal;
